/* eslint-disable no-unused-vars */
// ══════════════════════════════════════════════════════════════
// Content Dashboard v5
// + Heute Tab
// + 3-stufiger Status (Planned → Filmed → Gepostet)
// + Serien-Planung mit Deadlines & Ampel
// Franz & The Green Collective
// ══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchSeries, fetchReels,
  addSeries, deleteSeries,
  addReel, deleteReel,
  updateReelStatus,
  saveAnalytics, bulkImportReels,
  updateReelDriveLink, updateReelPostedAt,
  fetchAnalyticsForReel, fetchProfiles, fetchAllAnalytics,
  signOut, getSession, onAuthChange, getMyProfile,
  updateReel, changePassword,
} from "./supabaseClient";
import Auth from "./Auth";
import Overview from "./Overview";
import AnalyticsPage from "./AnalyticsPage";
import ContentPlan from "./ContentPlan";
import {
  FRANZ, TGC, BUILD, BG, CARD, BORDER, TEXT, MUTED, SOFT, GREEN, AMBER, RED,
  F_BODY, F_DISPLAY, F_MONO, LBL, Chip, BrandDot, aColor, bc, brandName, formatDate, MONTH_NAMES,
  STATUS_FLOW, STATUS_LABEL, STATUS_COLOR, STATUS_BG, hashtagsFor,
} from "./theme";

// ── Drive Folder Links ────────────────────────────────────────
// ── Google Drive ──────────────────────────────────────────────
// "02. Content" is sorted brand first, then month:
//   01. Raw Footage / <brand> / 2026-09 September
//   02. Final Reels / <brand> / 2026-09 September
// The month folders below are pre-created. A reel links straight to the
// folder for ITS posting month; if a month isn't listed (next year, or a
// folder nobody created yet) we fall back to the brand folder, which is
// one click away from the right month.
const DRIVE_ROOTS = {
  final: { franz: "1A6WIFIxGS36LZCmrluPL642tzW9mzogA", tgc: "1kU62QVkYCXCO-MKu-J_C3YXl0V6o1rFm" },
  raw:   { franz: "1KxVwCMqpjhakiAXO5nky2uLqgcWxvjMa", tgc: "1LwSblibySC-DK7iwojUE8EVrtpwOIH5p" },
};
const DRIVE_MONTHS = {
  final: {
    franz: {
      "2026-08": "15rhGl7LweV837Mw5L7RhZ35Pg3UjTOnn",
      "2026-09": "1Ed84YJHJK6Rb_qGZeAgZ494TRDAexF3b",
      "2026-10": "1h8VISyTxvRM-9X7xKy4cruFC82FpLDG1",
      "2026-11": "1NYsHeVqeLUP2A-HTJi9zhqN7_x_55dwV",
      "2026-12": "1TmXcAlFW5MkpelsU_i_05KRH5Sg9_u6m",
    },
    tgc: {
      "2026-08": "1rIap6UMnNRgm0HylYG_f243vlofItm-W",
      "2026-09": "1XhTqBABH16085YZr4A9L-3D8ELc9j7lE",
      "2026-10": "1id7p7kZNJfYtmdZxJbrOXslX7fXQ8bI2",
      "2026-11": "1b_5-iLIBC6WUJ_yDI4Y4fGWqubQXMXna",
      "2026-12": "1CwuXwNL4OnGn4XGX7RFuIf1cdS9rWeWE",
    },
  },
  raw: {
    franz: {
      "2026-08": "1M6RsIjlqAHGa1GoIs9bObNjUGrqa23rH",
      "2026-09": "1OIB6Xb2O5KQpyKA2Ew_kYZom_798iIvB",
      "2026-10": "1HrSsh99D7HQdsRGeZRVVN_ycY3sgTeo8",
      "2026-11": "1PwM-v0xXRMGSgWW8Vsf6q--fl-D3Repo",
      "2026-12": "1hlSQ6IJanXcKdHreRYh4ZcUXG4k76jTS",
    },
    tgc: {
      "2026-08": "1Ntjqua9iSNOkC6qUQPSoq-xKyoqavnAd",
      "2026-09": "1_nbKJ41-ZDzUWR8J4OyFbxhXUhULFLEV",
      "2026-10": "13msN3KYdXblk_vJJMGRAJsZXspTsZBPB",
      "2026-11": "12LIhFFDae_kO3OVUjyEr051u2-hg8qB7",
      "2026-12": "1pnPc23XLe6jgPmQKf1z6Ss-GmtUOnUy6",
    },
  },
};

function driveFolderUrl(kind, brand, dateISO) {
  const b  = (brand || "").toLowerCase() === "tgc" ? "tgc" : "franz";
  const id = DRIVE_MONTHS[kind][b][(dateISO || "").slice(0, 7)] || DRIVE_ROOTS[kind][b];
  return `https://drive.google.com/drive/folders/${id}`;
}

// The upload name from the Drive guide: 2026-09-03_Ando_matcha-asmr.mp4
// Date first, so the folder sorts itself into posting order.
function suggestedFileName(reel) {
  const slug = String(reel?.title || "reel")
    .toLowerCase()
    .replace(/\u00df/g, "ss")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .split("-").filter(Boolean).slice(0, 4).join("-") || "reel";
  const owner = (reel?.assignee || "").trim();
  const who   = owner ? owner[0].toUpperCase() + owner.slice(1).toLowerCase() : "Unassigned";
  return `${reel?.date || "0000-00-00"}_${who}_${slug}.mp4`;
}

function getDaysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function getStartDay(y, m)    { return new Date(y, m, 1).getDay(); }

function useIsMobile(bp = 640) {
  const [m, setM] = useState(() => typeof window !== "undefined" ? window.innerWidth < bp : false);
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return m;
}

// Swipe-right-from-left-edge to close (iOS-style back gesture)
function useSwipeBack(onClose) {
  useEffect(() => {
    let startX = null;
    let startY = null;
    const onStart = (e) => {
      const t = e.touches[0];
      // Only register if swipe starts near left edge
      if (t.clientX < 30) {
        startX = t.clientX;
        startY = t.clientY;
      }
    };
    const onMove = (e) => {
      if (startX === null) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      // Horizontal swipe > 80px and not too vertical
      if (dx > 80 && dy < 50) {
        startX = null;
        onClose();
      }
    };
    const onEnd = () => { startX = null; startY = null; };
    window.addEventListener("touchstart", onStart, { passive:true });
    window.addEventListener("touchmove",  onMove,  { passive:true });
    window.addEventListener("touchend",   onEnd,   { passive:true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove",  onMove);
      window.removeEventListener("touchend",   onEnd);
    };
  }, [onClose]);
}

const uid = () => Math.random().toString(36).slice(2, 8);

// ── Status helpers ────────────────────────────────────────────
const nextStatus    = (s) => { const i = STATUS_FLOW.indexOf(s); return i < 2 ? STATUS_FLOW[i+1] : STATUS_FLOW[i]; };
const prevStatus    = (s) => { const i = STATUS_FLOW.indexOf(s); return i > 0 ? STATUS_FLOW[i-1] : STATUS_FLOW[i]; };


// ── Bulk import: real CSV parser + header mapping ─────────────
// Handles quoted fields (commas/quotes/newlines inside cells) and maps both
// the app template AND the monthly Google-Sheet export (Date/Owner/Pillar/
// Format/Working Title/Concept/Hook/Link/Note/Approved by) onto reel fields.
function parseCSV(text) {
  const rows = []; let row = [], field = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i+1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ',') { row.push(field); field = ""; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i+1] === '\n') i++;
      row.push(field); field = "";
      if (row.some(v => v.trim() !== "")) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some(v => v.trim() !== "")) rows.push(row);
  return rows;
}

const normDate = (raw) => {
  const v = (raw || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const md = v.match(/^(\d{1,2})[./](\d{1,2})[./](\d{2,4})$/); // 26.08.26 / 26.08.2026
  if (md) {
    const yy = md[3].length === 2 ? "20" + md[3] : md[3];
    return `${yy}-${md[2].padStart(2, "0")}-${md[1].padStart(2, "0")}`;
  }
  return null;
};

function normalizeBulkRows(text, defaultBrand = "franz") {
  const raw = parseCSV(text);
  if (raw.length < 2) return { reels: [], errors: ["No data rows found."] };
  const keyOf = (h) => (h || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const headers = raw[0].map(keyOf);
  const FIELD = (k) => {
    if (k === "date" || k === "postingdate") return "date";
    if (k === "owner" || k === "assignee") return "assignee";
    if (k === "pillar") return "pillar";
    if (k === "format") return "format";
    if (k === "workingtitle" || k === "title") return "title";
    if (k === "concept" || k === "description" || k === "whattofilm") return "description";
    if (k.startsWith("hook")) return "hook";
    if (k === "link" || k === "referencelink" || k === "reference") return "reference_link";
    if (k === "caption") return "caption";
    if (k === "note" || k === "notes" || k === "directorsnote") return "notes";
    if (k === "approvedby" || k === "approved") return "approvedRaw";
    // "Estimated Lenght" — the sheet's spelling, kept working on purpose.
    if (k.includes("length") || k.includes("lenght")) return "est_length";
    if (["type","brand","series","part"].includes(k)) return k;
    return null;
  };
  const reelsOut = [], errors = [];
  raw.slice(1).forEach((cells, idx) => {
    const o = {};
    headers.forEach((h, i) => { const f = FIELD(h); if (f && cells[i] != null && cells[i].trim() !== "") o[f] = cells[i].trim(); });
    const line = idx + 2;
    const date = normDate(o.date);
    if (!date) { errors.push(`Row ${line}: missing/invalid date ("${o.date || ""}").`); return; }
    // Stories are no longer part of the plan — drop those rows quietly.
    if ((o.type || "").toUpperCase() === "STORY") return;
    // The sheet often leaves "Working Title" blank and carries the idea in
    // "Concept" — fall back to it instead of dropping the row.
    if (!o.title && o.description) { o.title = o.description.split(/[.\n]/)[0].trim().slice(0, 90); }
    if (!o.title) { errors.push(`Row ${line}: no title and no concept — skipped.`); return; }
    const apr = (o.approvedRaw || "").toLowerCase();
    reelsOut.push({
      brand: o.brand || defaultBrand, date,
      type: (o.type || "").toUpperCase() === "SERIES" || o.series ? "SERIES" : "REEL",
      title: o.title, hook: o.hook, description: o.description, format: o.format,
      caption: o.caption, notes: o.notes, series: o.series, part: o.part,
      assignee: o.assignee, pillar: o.pillar, est_length: o.est_length,
      reference_link: o.reference_link,
      approved: !!apr && !["no","false","0"].includes(apr),
    });
  });
  return { reels: reelsOut, errors };
}

// Days until date
const daysUntil = (dateStr) => {
  const today = new Date(); today.setHours(0,0,0,0);
  const target = new Date(dateStr + "T00:00:00");
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
};

// Ampel color based on days
const deadlineColor = (days) => {
  if (days < 0)  return RED;
  if (days <= 2) return RED;
  if (days <= 5) return AMBER;
  return GREEN;
};
const deadlineLabel = (days) => {
  if (days < 0)  return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `in ${days} days`;
};

// ══════════════════════════════════════════════════════════════
// SHARED UI
// ══════════════════════════════════════════════════════════════

const Input = ({ value, onChange, placeholder, style = {} }) => (
  <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    style={{ padding:"10px 12px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:6, color:TEXT, fontSize:14, fontFamily:F_MONO, outline:"none", width:"100%", boxSizing:"border-box", minHeight:44, ...style }}/>
);

const Btn = ({ onClick, children, accent, fill, disabled, style = {} }) => (
  <button onClick={onClick} disabled={disabled} style={{ padding:"10px 16px", borderRadius:6, border:`1px solid ${disabled?BORDER:accent||"#999"}`, background:fill?(accent||"#333"):"transparent", color:disabled?MUTED:fill?"#fff":accent||MUTED, fontSize:12, fontFamily:F_MONO, cursor:disabled?"default":"pointer", letterSpacing:"1px", transition:"all 0.15s", opacity:disabled?0.5:1, minHeight:44, ...style }}>{children}</button>
);

const BrandToggle = ({ brand, active, onClick, compact }) => (
  <button onClick={onClick} style={{ padding:compact?"8px 14px":"9px 22px", borderRadius:8, border:`1px solid ${active?bc(brand):BORDER}`, background:active?bc(brand):"transparent", color:active?"#fff":MUTED, fontSize:compact?11:12, fontFamily:F_MONO, letterSpacing:"1px", cursor:"pointer", transition:"all 0.15s", flex:1, minHeight:44 }}>
    {brand === "franz" ? "FRANZ" : compact ? "TGC" : "THE GREEN COLLECTIVE"}
  </button>
);

const Spinner = ({ color = FRANZ }) => (
  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:40 }}>
    <div style={{ width:32, height:32, border:`3px solid ${SOFT}`, borderTop:`3px solid ${color}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const ErrorBanner = ({ msg, onDismiss }) => (
  <div style={{ background:"#FFF0F0", border:"1px solid #FFCCCC", borderRadius:8, padding:"12px 16px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
    <span style={{ fontSize:13, color:"#CC3333", fontFamily:F_MONO }}>⚠️  {msg}</span>
    <button onClick={onDismiss} style={{ background:"none", border:"none", color:MUTED, cursor:"pointer", fontSize:16 }}>✕</button>
  </div>
);

// ── Status Badge ──────────────────────────────────────────────
function StatusBadge({ status, onClick, disabled }) {
  const color = STATUS_COLOR[status] || MUTED;
  const bg    = STATUS_BG[status]    || "transparent";
  const label = STATUS_LABEL[status] || status;
  return (
    <button onClick={onClick} disabled={disabled} style={{ padding:"4px 10px", borderRadius:20, border:`1.5px solid ${color}`, background:bg, color, fontSize:10, fontFamily:F_MONO, fontWeight:700, cursor:disabled?"default":"pointer", letterSpacing:"0.5px", transition:"all 0.15s", whiteSpace:"nowrap" }}>
      {status === "planned" && "○ "}
      {status === "filmed"  && "◑ "}
      {status === "posted"  && "● "}
      {label}
    </button>
  );
}

// ── DatePicker ────────────────────────────────────────────────
function DatePicker({ value, onChange, accentColor }) {
  const [open, setOpen] = useState(false);
  const today = new Date();
  const [vY, setVY] = useState(value ? +value.split("-")[0] : today.getFullYear());
  const [vM, setVM] = useState(value ? +value.split("-")[1] - 1 : today.getMonth());
  const ref = useRef(null);
  const accent = accentColor || FRANZ;

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const days = getDaysInMonth(vY, vM), sd = getStartDay(vY, vM);
  const pick = (d) => { onChange(`${vY}-${String(vM+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`); setOpen(false); };
  const pm   = () => { if(vM===0){setVM(11);setVY(y=>y-1);}else setVM(m=>m-1); };
  const nm   = () => { if(vM===11){setVM(0);setVY(y=>y+1);}else setVM(m=>m+1); };
  const sel  = value && value.startsWith(`${vY}-${String(vM+1).padStart(2,"0")}`) ? +value.split("-")[2] : null;

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <div onClick={() => setOpen(o=>!o)} style={{ padding:"10px 12px", background:SOFT, border:`1px solid ${open?accent:BORDER}`, borderRadius:6, color:value?TEXT:MUTED, fontSize:14, fontFamily:F_MONO, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", minHeight:44 }}>
        <span>{value ? formatDate(value) : "Pick a date"}</span><span>📅</span>
      </div>
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, zIndex:2000, background:CARD, border:`1px solid ${BORDER}`, borderRadius:12, padding:14, boxShadow:"0 8px 32px rgba(0,0,0,0.18)", minWidth:260, width:"100%" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <button onClick={pm} style={{ background:"none", border:`1px solid ${BORDER}`, borderRadius:6, width:32, height:32, cursor:"pointer", color:MUTED, fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>
            <span style={{ fontSize:13, fontWeight:700, color:TEXT, fontFamily:F_MONO }}>{MONTH_NAMES[vM].slice(0,3)} {vY}</span>
            <button onClick={nm} style={{ background:"none", border:`1px solid ${BORDER}`, borderRadius:6, width:32, height:32, cursor:"pointer", color:MUTED, fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }}>›</button>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:4 }}>
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <div key={d} style={{ textAlign:"center", fontSize:9, color:MUTED, fontFamily:F_MONO, padding:"2px 0" }}>{d}</div>)}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
            {Array.from({length:sd}).map((_,i) => <div key={`e${i}`}/>)}
            {Array.from({length:days}).map((_,i) => {
              const d=i+1, s=d===sel;
              return <div key={d} onClick={() => pick(d)} style={{ textAlign:"center", padding:"6px 2px", borderRadius:6, cursor:"pointer", fontSize:12, fontFamily:F_MONO, fontWeight:s?700:400, background:s?accent:"transparent", color:s?"#fff":TEXT, transition:"all 0.1s" }} onMouseEnter={e=>!s&&(e.currentTarget.style.background=`${accent}22`)} onMouseLeave={e=>!s&&(e.currentTarget.style.background="transparent")}>{d}</div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────
function Modal({ title, onClose, onSave, saving, children, wide }) {
  const m = useIsMobile();
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:m?"flex-end":"center", justifyContent:"center", padding:m?0:20 }}>
      <div style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:m?"16px 16px 0 0":"16px", padding:m?"20px 16px 36px":"32px", width:m?"100%":wide?680:520, maxWidth:"100%", maxHeight:m?"92vh":"90vh", overflowY:"auto", boxShadow:"0 -8px 40px rgba(0,0,0,0.18)" }}>
        {m && <div style={{ width:40, height:4, background:BORDER, borderRadius:2, margin:"0 auto 16px" }}/>}
        <div style={{ fontSize:16, fontWeight:700, color:TEXT, marginBottom:18 }}>{title}</div>
        {children}
        {onSave && <div style={{ display:"flex", gap:10, marginTop:20, justifyContent:"flex-end" }}><Btn onClick={onClose} disabled={saving}>CANCEL</Btn><Btn onClick={onSave} accent={FRANZ} fill disabled={saving}>{saving?"SAVING...":"SAVE"}</Btn></div>}
        {!onSave && <div style={{ display:"flex", justifyContent:"flex-end", marginTop:20 }}><Btn onClick={onClose} accent={MUTED}>CLOSE</Btn></div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// HEADER
// Same layout as the Society dashboard: title top-left, account
// top-right, brand pills below, tabs as an underlined row underneath.
// ══════════════════════════════════════════════════════════════
export function Header({ m, saving, todayLong, who, whoTitle, role, brand, setBrand, tab, setTab, tabs, onPassword, onSignOut }) {
  const accent = brand === "all" ? TEXT : bc(brand);
  const acct = {
    marginLeft:8, fontSize:12, fontWeight:600, padding:"3px 10px", borderRadius:99,
    border:`1px solid ${BORDER}`, background:CARD, color:TEXT, cursor:"pointer",
  };
  return (
    <div style={{ maxWidth:1200, margin:"0 auto", padding:m?"max(14px, env(safe-area-inset-top)) 12px 0":"26px 28px 0" }}>

      {/* Row 1 — who we are (left) · who you are (far right) */}
      <div style={{ display:"flex", flexWrap:"wrap", alignItems:"flex-end", justifyContent:"space-between", gap:14, marginBottom:6 }}>
        <div style={{ minWidth:0 }}>
          <div style={LBL}>fr-anz · The Green Collective</div>
          <h1 style={{ margin:"6px 0 0", fontSize:m?23:30, fontWeight:700, letterSpacing:"-0.02em", textTransform:"lowercase", fontFamily:F_DISPLAY, color:TEXT, display:"flex", alignItems:"center", gap:9 }}>
            content dashboard
            {saving && <span style={{ width:7, height:7, borderRadius:"50%", background:FRANZ, animation:"pulse 1s infinite" }}/>}
          </h1>
          <div style={{ fontSize:12.5, color:MUTED, marginTop:4 }}>{todayLong}</div>
        </div>
        {who && (
          <div style={{ textAlign:"right", fontSize:12, color:MUTED, lineHeight:1.9 }}>
            <span title={whoTitle}>{who} · {role==="admin" ? "Admin" : "Creator"}</span>
            <button onClick={onPassword} style={{ ...acct, marginLeft:10 }}>Change password</button>
            <button onClick={onSignOut} style={acct}>Sign out</button>
          </div>
        )}
      </div>

      {/* Row 2 — brand switch */}
      <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:10, margin:"18px 0 14px" }}>
        {[["all","All brands"],["franz","fr-anz"],["tgc","The Green Collective"]].map(([id,label])=>{
          const active = brand===id, c = id==="all" ? TEXT : bc(id);
          return (
            <button key={id} onClick={()=>setBrand(id)}
              style={{ fontSize:13, fontWeight:600, padding:m?"7px 13px":"8px 16px", borderRadius:99, cursor:"pointer",
                border:`1.5px solid ${active?c:BORDER}`, background:active?c:"transparent", color:active?"#fff":TEXT, transition:"all .2s" }}>
              {label}
            </button>
          );
        })}
      </div>

      {/* Row 3 — tabs. On mobile the bottom nav does this job instead. */}
      {!m && (
        <div style={{ display:"flex", gap:0, borderBottom:`1px solid ${BORDER}`, flexWrap:"nowrap", overflowX:"auto", overflowY:"hidden", scrollbarWidth:"none" }}>
          {tabs.map(([id,,label])=>(
            <button key={id} onClick={()=>setTab(id)}
              style={{ fontSize:14, fontWeight:600, padding:"10px 13px", border:"none", background:"transparent", cursor:"pointer",
                color: tab===id ? TEXT : MUTED,
                borderBottom: tab===id ? `2.5px solid ${accent}` : "2.5px solid transparent",
                marginBottom:-1, whiteSpace:"nowrap", flexShrink:0 }}>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SerienTab({ series, reels, onOpenReel, onToggleStatus, saving, role, onAdd, onDelete }) {
  const m = useIsMobile();
  const [expandedSeries, setExpandedSeries] = useState(null);
  const isAdmin = role === "admin";
  const [adding, setAdding] = useState(false);
  const [ns, setNs] = useState({ id:"", name:"", brand:"Both", color:"#C4527A", parts:3 });
  const submitNew = () => {
    const id = (ns.id || ns.name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    if (!id || !ns.name) return;
    onAdd({ ...ns, id });
    setNs({ id:"", name:"", brand:"Both", color:"#C4527A", parts:3 });
    setAdding(false);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {isAdmin && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {!adding ? (
            <button onClick={()=>setAdding(true)} style={{ alignSelf:"flex-start", padding:"8px 16px", borderRadius:8, border:`1px solid ${FRANZ}`, background:`${FRANZ}11`, color:FRANZ, fontSize:12, fontFamily:F_MONO, fontWeight:700, cursor:"pointer" }}>+ SERIES</button>
          ) : (
            <div style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:12, padding:16, display:"flex", flexWrap:"wrap", gap:10, alignItems:"center" }}>
              <input placeholder="Name" value={ns.name} onChange={e=>setNs(p=>({...p,name:e.target.value}))} style={{ padding:"9px 12px", borderRadius:8, border:`1px solid ${BORDER}`, fontSize:13, flex:"1 1 160px" }}/>
              <select value={ns.brand} onChange={e=>setNs(p=>({...p,brand:e.target.value}))} style={{ padding:"9px 12px", borderRadius:8, border:`1px solid ${BORDER}`, fontSize:13 }}>
                <option value="Both">Both</option><option value="Franz">Franz</option><option value="TGC">TGC</option>
              </select>
              <input type="number" min="1" placeholder="Parts" value={ns.parts} onChange={e=>setNs(p=>({...p,parts:e.target.value}))} style={{ padding:"9px 12px", borderRadius:8, border:`1px solid ${BORDER}`, fontSize:13, width:80 }}/>
              <input type="color" value={ns.color} onChange={e=>setNs(p=>({...p,color:e.target.value}))} title="Color" style={{ width:40, height:38, border:`1px solid ${BORDER}`, borderRadius:8, cursor:"pointer" }}/>
              <button onClick={submitNew} style={{ padding:"9px 16px", borderRadius:8, border:"none", background:TEXT, color:BG, fontSize:13, fontWeight:600, cursor:"pointer" }}>Add</button>
              <button onClick={()=>setAdding(false)} style={{ padding:"9px 12px", borderRadius:8, border:`1px solid ${BORDER}`, background:"transparent", color:MUTED, fontSize:13, cursor:"pointer" }}>Cancel</button>
            </div>
          )}
        </div>
      )}
      {series.map(s => {
        const brands  = s.brand==="Both" ? ["franz","tgc"] : [s.brand.toLowerCase()];
        // Show ALL reels matching this series (with or without date)
        const episodes = brands
          .flatMap(b => reels.filter(r => r.brand===b && (r.type==="SERIES" || r.series_id===s.id)).filter(r => r.series_id===s.id).map(r => ({...r,b})))
          .sort((a,b) => (a.part||0) - (b.part||0));
        const isExpanded = expandedSeries === s.id;

        const posted   = episodes.filter(e => e.status==="posted").length;
        const filmed   = episodes.filter(e => e.status==="filmed").length;
        const planned  = episodes.filter(e => e.status==="planned").length;
        const pct      = Math.round((posted / s.parts) * 100);

        // Next unposted episode
        const nextEp   = episodes.find(e => e.status !== "posted");
        const nextDays = nextEp ? daysUntil(nextEp.date) : null;
        const ampel    = nextEp ? deadlineColor(nextDays) : GREEN;

        return (
          <div key={s.id} 
            onClick={() => setExpandedSeries(isExpanded ? null : s.id)}
            style={{ background:CARD, border:`1px solid ${isExpanded?s.color:BORDER}`, borderLeft:`4px solid ${s.color}`, borderRadius:14, padding:m?14:22, boxShadow:isExpanded?`0 4px 16px ${s.color}22`:"0 1px 4px rgba(0,0,0,0.06)", cursor:"pointer", transition:"all 0.2s" }}>

            {/* Series Header */}
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:14 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                  <div style={{ fontSize:9, color:s.color, fontFamily:F_MONO, letterSpacing:"2px", fontWeight:700 }}>{s.brand.toUpperCase()}</div>
                  <span style={{ fontSize:14, color:s.color }}>{isExpanded ? "▼" : "▶"}</span>
                </div>
                <div style={{ fontSize:m?15:18, fontWeight:700, color:TEXT }}>{s.name}</div>
                <div style={{ fontSize:11, color:MUTED, fontFamily:F_MONO, marginTop:3 }}>
                  {posted} posted · {filmed} filmed · {planned} planned · {s.parts} total
                </div>
                {!isExpanded && episodes.length > 0 && <div style={{ fontSize:9, color:s.color, fontFamily:F_MONO, marginTop:6, letterSpacing:"1px" }}>TAP TO VIEW ALL {episodes.length} EPISODES →</div>}
              </div>
              {/* Ampel */}
              {nextEp && (
                <div style={{ textAlign:"center", flexShrink:0, marginLeft:12 }}>
                  <div style={{ width:44, height:44, borderRadius:"50%", background:ampel, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:3 }}>
                    <span style={{ fontSize:18 }}>
                      {ampel===GREEN?"✓":ampel===AMBER?"⚡":"🔴"}
                    </span>
                  </div>
                  <div style={{ fontSize:9, color:ampel, fontFamily:F_MONO, fontWeight:700, whiteSpace:"nowrap" }}>
                    {deadlineLabel(nextDays)}
                  </div>
                </div>
              )}
              {isAdmin && (
                <button onClick={(e)=>{ e.stopPropagation(); if(window.confirm(`Delete series "${s.name}"?`)) onDelete(s.id); }}
                  title="Delete series"
                  style={{ marginLeft:10, flexShrink:0, width:28, height:28, borderRadius:8, border:`1px solid ${BORDER}`, background:"transparent", color:MUTED, fontSize:14, cursor:"pointer", lineHeight:1 }}>✕</button>
              )}
            </div>

            {/* Progress bar */}
            <div style={{ height:6, background:SOFT, borderRadius:3, marginBottom:14, overflow:"hidden" }}>
              <div style={{ height:"100%", borderRadius:3, display:"flex" }}>
                <div style={{ width:`${pct}%`, background:s.color, transition:"width 0.3s" }}/>
                <div style={{ width:`${Math.round((filmed/s.parts)*100)}%`, background:`${s.color}55`, transition:"width 0.3s" }}/>
              </div>
            </div>

            {/* Episodes — only shown when expanded */}
            {isExpanded && <div style={{ display:"grid", gridTemplateColumns:m?"1fr":"1fr 1fr", gap:8 }}>
              {episodes.map((ep, i) => {
                const days  = daysUntil(ep.date);
                const dColor = ep.status==="posted" ? GREEN : deadlineColor(days);
                return (
                  <div key={i} onClick={(e) => { e.stopPropagation(); onOpenReel(ep, ep.b); }}
                    style={{ padding:"12px 14px", background:ep.status==="posted"?`${s.color}08`:SOFT, border:`1px solid ${ep.status==="posted"?s.color+"44":BORDER}`, borderRadius:10, cursor:"pointer", transition:"all 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor=s.color}
                    onMouseLeave={e => e.currentTarget.style.borderColor=ep.status==="posted"?s.color+"44":BORDER}>

                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:9, color:s.color, fontFamily:F_MONO, fontWeight:700 }}>TEIL {ep.part}</span>
                        <span style={{ fontSize:9, color:bc(ep.b), fontFamily:F_MONO }}>{ep.b==="franz"?"Franz":"TGC"}</span>
                      </div>
                      <StatusBadge status={ep.status} onClick={e => { e.stopPropagation(); onToggleStatus(ep.id, ep.status); }} disabled={saving}/>
                    </div>

                    <div style={{ fontSize:13, fontWeight:600, color:TEXT, marginBottom:4 }}>{ep.title}</div>

                    {/* Deadline */}
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:dColor, flexShrink:0 }}/>
                      <span style={{ fontSize:10, color:dColor, fontFamily:F_MONO, fontWeight:700 }}>
                        {formatDate(ep.date)} — {ep.status==="posted" ? "✓ Posted" : deadlineLabel(days)}
                      </span>
                    </div>

                    {/* Days between episodes */}
                    {i < episodes.length - 1 && (
                      <div style={{ marginTop:6, fontSize:9, color:MUTED, fontFamily:F_MONO }}>
                        → Part {ep.part+1} in {daysUntil(episodes[i+1].date) - daysUntil(ep.date)} days
                      </div>
                    )}
                  </div>
                );
              })}
            </div>}

            {isExpanded && episodes.length === 0 && (
              <div style={{ fontSize:12, color:MUTED, fontFamily:F_MONO }}>No episodes planned yet.</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// REEL DETAIL MODAL
// ══════════════════════════════════════════════════════════════
const OWNERS  = ["Ando", "Yugo"];
const PILLARS = ["Process", "BTS", "USP", "Episode", "Storytelling", "Photobooth", "Filler"];

// Dropdown variant of EditRow — used where the value comes from a fixed list.
function EditSelect({ label, value, onChange, options }) {
  return (
    <div>
      <div style={{ ...LBL, fontSize: 10, marginBottom: 5 }}>{label}</div>
      <select value={value || ""} onChange={e => onChange(e.target.value)}
        style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${BORDER}`, fontSize:14, boxSizing:"border-box", background:BG, color:TEXT, minHeight:42 }}>
        <option value="">—</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
        {value && !options.includes(value) && <option value={value}>{value}</option>}
      </select>
    </div>
  );
}

// Editable field row for the reel edit form. Module-level so it keeps input
// focus while typing (a component defined inside a parent remounts each render).
function EditRow({ label, value, onChange, area }) {
  const base = { width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${BORDER}`, fontSize:14, boxSizing:"border-box", background:"#FCFAF6", color:TEXT };
  return (
    <div>
      <div style={{ ...LBL, fontSize:10, marginBottom:5 }}>{label}</div>
      {area
        ? <textarea value={value} onChange={e=>onChange(e.target.value)} rows={3} style={{ ...base, fontFamily:"inherit", resize:"vertical" }}/>
        : <input value={value} onChange={e=>onChange(e.target.value)} style={base}/>}
    </div>
  );
}

function ReelDetail({ reel, brand, series, onClose, onToggleStatus, onSetStatus, onUpdateDriveLink, onUpdateReel, role, saving, analytics, onSaveAnalytics }) {
  const m     = useIsMobile();
  useSwipeBack(onClose);
  const color = bc(brand);
  const sObj  = reel.type==="SERIES" ? series.find(s=>s.id===reel.series_id) : null;
  const tc    = sObj?.color || color;
  const [av, setAv] = useState({ views:analytics?.views||"", likes:analytics?.likes||"", comments:analytics?.comments||"", shares:analytics?.shares||"", saves:analytics?.saves||"" });
  // Analytics arrives one round trip after the modal opens. Only seed the form
  // from it while the user hasn't typed yet — otherwise the late response would
  // wipe numbers they already entered.
  const avTouched = useRef(false);
  useEffect(() => {
    if (avTouched.current) return;
    setAv({ views:analytics?.views||"", likes:analytics?.likes||"", comments:analytics?.comments||"", shares:analytics?.shares||"", saves:analytics?.saves||"" });
  }, [analytics]);
  useEffect(() => { avTouched.current = false; }, [reel.id]);

  // Inline edit of the reel's content (title, hook, caption, …). Any logged-in user may edit.
  const [editing, setEditing] = useState(false);
  const [ef, setEf] = useState({ title:reel.title||"", hook:reel.hook||"", description:reel.description||"", format:reel.format||"", caption:reel.caption||"", notes:reel.notes||"", date:reel.date||"", assignee:reel.assignee||"", pillar:reel.pillar||"", est_length:reel.est_length||"", reference_link:reel.reference_link||"" });
  useEffect(() => {
    setEf({ title:reel.title||"", hook:reel.hook||"", description:reel.description||"", format:reel.format||"", caption:reel.caption||"", notes:reel.notes||"", date:reel.date||"", assignee:reel.assignee||"", pillar:reel.pillar||"", est_length:reel.est_length||"", reference_link:reel.reference_link||"" });
    setEditing(false);
    // Intentionally keyed on reel.id only: re-running on every field change
    // would wipe what the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reel.id]);
  const [editErr, setEditErr] = useState(null);
  const saveEdit = async () => {
    setEditErr(null);
    if (!onUpdateReel) return;
    if (!ef.title.trim()) { setEditErr("Title can't be empty."); return; }
    // The date field is free text — normalise 26.08.26 / 26.08.2026 and reject
    // anything Postgres would refuse (reels.date is NOT NULL).
    const d = normDate(ef.date);
    if (!d) { setEditErr("Date must be a real date, e.g. 2026-09-26 or 26.09.26."); return; }
    const res = await onUpdateReel(reel.id, { ...ef, date: d });
    if (res && res.ok === false) { setEditErr(res.error || "Saving failed — nothing was changed."); return; }
    setEditing(false);
  };

  const IB = ({ label, value }) => value ? (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:10, color:MUTED, letterSpacing:"2px", textTransform:"uppercase", fontFamily:F_MONO, marginBottom:5 }}>{label}</div>
      <div style={{ fontSize:14, color:TEXT, lineHeight:1.7 }}>{value}</div>
    </div>
  ) : null;

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000, display:"flex", alignItems:m?"flex-end":"center", justifyContent:"center", padding:m?0:20 }}>
      <div style={{ background:CARD, borderTop:`4px solid ${color}`, borderRadius:m?"16px 16px 0 0":"16px", padding:m?"calc(env(safe-area-inset-top) + 28px) 16px 36px":"32px", width:m?"100%":640, maxWidth:"100%", maxHeight:m?"94vh":"90vh", overflowY:"auto", boxShadow:"0 -8px 40px rgba(0,0,0,0.2)" }}>
        {m && <div style={{ width:40, height:4, background:BORDER, borderRadius:2, margin:"0 auto 16px" }}/>}

        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:6, flexWrap:"wrap" }}>
              <div style={{ padding:"3px 10px", borderRadius:4, background:`${color}18`, border:`1px solid ${color}55`, fontSize:10, fontFamily:F_MONO, color }}>{brand==="franz"?"FRANZ":"TGC"} · {formatDate(reel.date)}</div>
              {reel.type==="SERIES"&&sObj&&<div style={{ padding:"3px 10px", borderRadius:4, background:`${tc}18`, border:`1px solid ${tc}55`, fontSize:10, fontFamily:F_MONO, color:tc }}>{sObj.name} · Pt {reel.part}</div>}
              <StatusBadge status={reel.status} onClick={() => onToggleStatus(reel.id, reel.status)} disabled={saving}/>
            </div>
            <div style={{ fontSize:m?20:24, fontWeight:700, color:TEXT }}>{reel.title}</div>
          </div>
          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
            {onUpdateReel && !editing && (
              <button onClick={()=>setEditing(true)} title="Edit" style={{ background:"none", border:`1px solid ${BORDER}`, color:MUTED, fontSize:13, cursor:"pointer", padding:"0 12px", height:44, borderRadius:8, fontFamily:F_MONO }}>Edit</button>
            )}
            <button onClick={onClose} style={{ background:"none", border:"none", color:MUTED, fontSize:22, cursor:"pointer", padding:"12px", lineHeight:1, minWidth:48, minHeight:48, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:8 }}>✕</button>
          </div>
        </div>

        <div style={{ height:1, background:BORDER, marginBottom:20 }}/>

        {editing ? (
          <div style={{ marginBottom:20, display:"flex", flexDirection:"column", gap:12 }}>
            <EditRow label="Title" value={ef.title} onChange={v=>setEf(p=>({...p,title:v}))}/>
            <div style={{ display:"grid", gridTemplateColumns:m?"1fr 1fr":"repeat(4, 1fr)", gap:10 }}>
              <EditRow label="Date (YYYY-MM-DD)" value={ef.date} onChange={v=>setEf(p=>({...p,date:v}))}/>
              <EditSelect label="Owner" value={ef.assignee} onChange={v=>setEf(p=>({...p,assignee:v}))} options={OWNERS}/>
              <EditSelect label="Pillar" value={ef.pillar} onChange={v=>setEf(p=>({...p,pillar:v}))} options={PILLARS}/>
              <EditRow label="Est. length" value={ef.est_length} onChange={v=>setEf(p=>({...p,est_length:v}))}/>
            </div>
            <EditRow label="Hook — first 2 seconds" value={ef.hook} onChange={v=>setEf(p=>({...p,hook:v}))} area/>
            <EditRow label="What to film" value={ef.description} onChange={v=>setEf(p=>({...p,description:v}))} area/>
            <EditRow label="Format & Style" value={ef.format} onChange={v=>setEf(p=>({...p,format:v}))} area/>
            <EditRow label="Caption" value={ef.caption} onChange={v=>setEf(p=>({...p,caption:v}))} area/>
            <EditRow label="Reference link" value={ef.reference_link} onChange={v=>setEf(p=>({...p,reference_link:v}))}/>
            <EditRow label="Director's Note" value={ef.notes} onChange={v=>setEf(p=>({...p,notes:v}))} area/>
            {editErr && <div style={{ fontSize:13, color:RED, background:`${RED}0F`, border:`1px solid ${RED}44`, borderRadius:8, padding:"9px 12px" }}>{editErr}</div>}
            <div style={{ display:"flex", gap:8, marginTop:4 }}>
              <button onClick={saveEdit} disabled={saving} style={{ padding:"11px 20px", borderRadius:8, border:"none", background:TEXT, color:BG, fontSize:14, fontWeight:600, cursor:saving?"default":"pointer", opacity:saving?0.6:1 }}>{saving?"Saving…":"Save"}</button>
              <button onClick={()=>setEditing(false)} style={{ padding:"11px 16px", borderRadius:8, border:`1px solid ${BORDER}`, background:"transparent", color:MUTED, fontSize:14, cursor:"pointer" }}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            {/* Meta grid — the content-plan facts at a glance */}
            <div style={{ display:"grid", gridTemplateColumns:m?"1fr 1fr":"repeat(3, 1fr)", gap:10, marginBottom:18 }}>
              {[["Posting date", formatDate(reel.date)],
                ["Owner", reel.assignee || "—"],
                ["Pillar", reel.pillar || "—"],
                ["Format", reel.format || "—"],
                ["Est. length", reel.est_length || "—"],
                ["Status", STATUS_LABEL[reel.status]]].map(([l, v]) => (
                <div key={l} style={{ background:SOFT, border:`1px solid ${BORDER}`, borderRadius:10, padding:"10px 12px" }}>
                  <div style={{ ...LBL, fontSize:9.5, marginBottom:4 }}>{l}</div>
                  <div style={{ fontSize:14, fontWeight:600, color: l==="Owner" ? aColor(reel.assignee) : TEXT }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Approval */}
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:18, padding:"12px 14px", borderRadius:10, background: reel.approved ? `${GREEN}0D` : `${AMBER}0D`, border:`1px solid ${reel.approved ? GREEN : AMBER}44` }}>
              <Chip text={reel.approved ? "✓ Approved" : "Pending approval"} color={reel.approved ? GREEN : AMBER} filled/>
              <div style={{ flex:1, fontSize:12.5, color:MUTED }}>{reel.approved ? "This piece is signed off for the monthly plan." : "Not signed off yet."}</div>
              {role === "admin" && onUpdateReel && (
                <button disabled={saving} onClick={() => onUpdateReel(reel.id, { approved: !reel.approved })}
                  style={{ padding:"8px 14px", borderRadius:8, border:`1px solid ${reel.approved ? BORDER : GREEN}`, background: reel.approved ? "transparent" : GREEN, color: reel.approved ? MUTED : "#fff", fontSize:12.5, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap" }}>
                  {reel.approved ? "Revoke" : "Approve"}
                </button>
              )}
            </div>

            <IB label="Hook — first 2 seconds" value={reel.hook}/>
            <IB label="Concept — what to film" value={reel.description}/>
            {reel.reference_link && (
              <div style={{ marginBottom:16 }}>
                <div style={{ ...LBL, fontSize:10, marginBottom:5 }}>Reference</div>
                <a href={reel.reference_link} target="_blank" rel="noopener noreferrer" style={{ fontSize:13.5, color:BUILD, wordBreak:"break-all" }}>{reel.reference_link}</a>
              </div>
            )}

            {reel.notes && (
              <div style={{ marginBottom:16, padding:"14px 16px", background:`${color}0F`, border:`1px solid ${color}33`, borderRadius:10 }}>
                <div style={{ fontSize:10, color, letterSpacing:"2px", textTransform:"uppercase", fontFamily:F_MONO, marginBottom:6 }}>Director's Note</div>
                <div style={{ fontSize:13, color:TEXT, lineHeight:1.7, fontStyle:"italic" }}>{reel.notes}</div>
              </div>
            )}

            {reel.caption && (
              <div style={{ marginBottom:16, padding:"12px 16px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:10 }}>
                <div style={{ ...LBL, fontSize:10, marginBottom:6 }}>Caption</div>
                <div style={{ fontSize:14, color:TEXT, fontStyle:"italic" }}>"{reel.caption}"</div>
              </div>
            )}

            {/* Hashtag suggestions — tap to copy the whole set */}
            <div style={{ marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:7 }}>
                <div style={{ ...LBL, fontSize:10 }}>Hashtag suggestions</div>
                <button onClick={()=>{ navigator.clipboard.writeText(hashtagsFor(reel).join(" ")); window.alert("Hashtags copied"); }}
                  style={{ padding:"4px 10px", borderRadius:8, border:`1px solid ${BORDER}`, background:"transparent", color:MUTED, fontSize:11.5, fontWeight:600, cursor:"pointer" }}>Copy all</button>
              </div>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                {hashtagsFor(reel).map(h => (
                  <span key={h} onClick={()=>navigator.clipboard.writeText(h)}
                    style={{ padding:"3px 9px", borderRadius:99, border:`1px solid ${BORDER}`, background:CARD, color:MUTED, fontSize:11.5, cursor:"pointer" }}>{h}</span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Status — any of the 3 stages can be clicked to set directly */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:10, color:MUTED, letterSpacing:"2px", textTransform:"uppercase", fontFamily:F_MONO, marginBottom:10 }}>STATUS — TAP TO CHANGE</div>
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
            {STATUS_FLOW.map((s, i) => {
              const isActive = reel.status === s;
              const sColor   = STATUS_COLOR[s];
              return (
                <button key={s} disabled={saving}
                  onClick={() => !saving && onSetStatus && onSetStatus(reel.id, s)}
                  style={{ padding:"10px 18px", borderRadius:24, border:`2px solid ${isActive?sColor:BORDER}`, background:isActive?sColor:"transparent", color:isActive?"#fff":sColor, fontSize:12, fontFamily:F_MONO, fontWeight:isActive?700:600, cursor:saving?"default":"pointer", transition:"all 0.15s", minHeight:44, letterSpacing:"0.5px" }}>
                  {s === "planned" && "○ "}{s === "filmed" && "◑ "}{s === "posted" && "● "}
                  {STATUS_LABEL[s]}
                </button>
              );
            })}
          </div>
          {reel.posted_at && (
            <div style={{ fontSize:10, color:MUTED, fontFamily:F_MONO, marginTop:8 }}>
              ✓ Posted: {new Date(reel.posted_at).toLocaleString("en-GB", {day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}
            </div>
          )}
        </div>

        {/* File Name — the convention from the Drive guide, ready to copy */}
        {(() => {
          const fileName = reel.file_name || suggestedFileName(reel);
          return (
            <div style={{ marginBottom:16, padding:"12px 14px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:10 }}>
              <div style={{ fontSize:10, color:MUTED, letterSpacing:"2px", textTransform:"uppercase", fontFamily:F_MONO, marginBottom:6 }}>FILE NAME (TAP TO COPY)</div>
              <div onClick={() => {
                navigator.clipboard.writeText(fileName);
                window.alert("Copied: " + fileName);
              }} style={{ fontSize:12, color:TEXT, fontFamily:F_MONO, padding:"8px 10px", background:CARD, border:`1px solid ${BORDER}`, borderRadius:6, cursor:"pointer", wordBreak:"break-all" }}>
                {fileName} 📋
              </div>
            </div>
          );
        })()}

        {/* Drive Folder Section */}
        <div style={{ marginBottom:20, padding:m?"14px":"16px 18px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:12 }}>
          <div style={{ fontSize:10, color:MUTED, letterSpacing:"2px", textTransform:"uppercase", fontFamily:F_MONO, marginBottom:10 }}>📁 GOOGLE DRIVE</div>

          <div style={{ fontSize:12, color:TEXT, lineHeight:1.5, marginBottom:12 }}>
            Upload the final reel under the file name above. The buttons open this reel's own month folder — {brand?.toLowerCase()==="tgc" ? "The Green Collective" : "fr-anz"} · {reel.date ? `${MONTH_NAMES[Number(reel.date.slice(5,7))-1]} ${reel.date.slice(0,4)}` : "no date set"}.
          </div>

          {/* Folder Buttons */}
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:12 }}>
            <a href={driveFolderUrl("final", brand, reel.date)} target="_blank" rel="noopener noreferrer"
              style={{ display:"inline-block", padding:"10px 16px", background:brand?.toLowerCase()==="tgc"?TGC:FRANZ, color:"#fff", borderRadius:8, textDecoration:"none", fontSize:12, fontFamily:F_MONO, fontWeight:600 }}>
              📁 FINAL REELS
            </a>
            <a href={driveFolderUrl("raw", brand, reel.date)} target="_blank" rel="noopener noreferrer"
              style={{ display:"inline-block", padding:"10px 16px", background:"transparent", border:`1px solid ${BORDER}`, color:MUTED, borderRadius:8, textDecoration:"none", fontSize:12, fontFamily:F_MONO, fontWeight:600 }}>
              📂 RAW FOOTAGE
            </a>
          </div>

          {/* Optional Drive Link to specific file */}
          <div style={{ fontSize:10, color:MUTED, letterSpacing:"2px", textTransform:"uppercase", fontFamily:F_MONO, marginBottom:6, marginTop:14 }}>DIRECT LINK TO FILE (OPTIONAL)</div>
          {reel.drive_link ? (
            <div>
              <a href={reel.drive_link} target="_blank" rel="noopener noreferrer"
                style={{ display:"inline-block", padding:"8px 14px", background:BUILD, color:"#fff", borderRadius:8, textDecoration:"none", fontSize:11, fontFamily:F_MONO, fontWeight:600, marginRight:8 }}>
                ▶ VIEW THIS REEL
              </a>
              <button onClick={async () => {
                if (window.confirm("Remove drive link?")) {
                  await onUpdateDriveLink && onUpdateDriveLink(reel.id, "");
                }
              }} style={{ padding:"8px 12px", background:"transparent", border:`1px solid ${BORDER}`, color:MUTED, fontSize:10, fontFamily:F_MONO, cursor:"pointer", borderRadius:8 }}>
                ✕ Remove
              </button>
              <div style={{ fontSize:10, color:MUTED, marginTop:8, fontFamily:F_MONO, wordBreak:"break-all" }}>{reel.drive_link}</div>
            </div>
          ) : (
            <div>
              <input type="text" placeholder="Paste link to specific file (optional)..."
                onKeyDown={async (e) => {
                  if (e.key === "Enter" && e.target.value.trim()) {
                    await onUpdateDriveLink && onUpdateDriveLink(reel.id, e.target.value.trim());
                    e.target.value = "";
                  }
                }}
                onBlur={async (e) => {
                  if (e.target.value.trim()) {
                    await onUpdateDriveLink && onUpdateDriveLink(reel.id, e.target.value.trim());
                    e.target.value = "";
                  }
                }}
                style={{ width:"100%", padding:"8px 10px", border:`1px solid ${BORDER}`, borderRadius:8, fontSize:11, fontFamily:F_MONO, background:CARD, color:TEXT, boxSizing:"border-box" }}/>
              <div style={{ fontSize:10, color:MUTED, fontFamily:F_MONO, marginTop:4 }}>
                Auto-sets status to "Filmed" when link is added
              </div>
            </div>
          )}
        </div>

        {/* Analytics */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:10, color:MUTED, letterSpacing:"2px", textTransform:"uppercase", fontFamily:F_MONO, marginBottom:10 }}>Analytics</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {["views","likes","comments","shares","saves"].map(metric => (
              <div key={metric}>
                <div style={{ fontSize:9, color:MUTED, fontFamily:F_MONO, textTransform:"uppercase", marginBottom:4 }}>{metric}</div>
                <input type="number" placeholder="—" value={av[metric]} onChange={e => { avTouched.current = true; setAv(p=>({...p,[metric]:e.target.value})); }}
                  style={{ width:"100%", padding:"10px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:6, color:TEXT, fontSize:14, fontFamily:F_MONO, boxSizing:"border-box", minHeight:44 }}/>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display:"flex", flexDirection:m?"column":"row", gap:10, justifyContent:"flex-end" }}>
          {!m && <Btn onClick={onClose} accent={MUTED}>CLOSE</Btn>}
          <Btn onClick={() => onSaveAnalytics(reel.id, av)} accent={MUTED} disabled={saving}>{saving?"SAVING...":"SAVE ANALYTICS"}</Btn>
          {m && <Btn onClick={onClose} accent={MUTED}>CLOSE</Btn>}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// CALENDAR GRID
// ══════════════════════════════════════════════════════════════
export function CalendarGrid({ reels, onDayClick }) {
  const m = useIsMobile(), now = new Date();
  const [vY, setVY] = useState(now.getFullYear()), [vM, setVM] = useState(now.getMonth());
  const [viewMode, setViewMode] = useState("week"); // "week" or "month"
  const [weekStart, setWeekStart] = useState(() => {
    // Start of current week (Monday)
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day; // adjust to Monday
    d.setDate(d.getDate() + diff);
    d.setHours(0,0,0,0);
    return d;
  });

  const days = getDaysInMonth(vY, vM), sd = getStartDay(vY, vM);
  const tod  = now.getFullYear()===vY && now.getMonth()===vM ? now.getDate() : null;
  const pm   = () => { if(vM===0){setVM(11);setVY(y=>y-1);}else setVM(v=>v-1); };
  const nm   = () => { if(vM===11){setVM(0);setVY(y=>y+1);}else setVM(v=>v+1); };
  const ds   = (d) => `${vY}-${String(vM+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;

  // Week navigation
  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate()-7); setWeekStart(d); };
  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate()+7); setWeekStart(d); };
  const formatDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

  // Get 7 days starting from weekStart
  const weekDays = Array.from({length:7}, (_,i) => { const d = new Date(weekStart); d.setDate(d.getDate()+i); return d; });
  const weekRange = `${weekDays[0].getDate()} ${MONTH_NAMES[weekDays[0].getMonth()].slice(0,3)} – ${weekDays[6].getDate()} ${MONTH_NAMES[weekDays[6].getMonth()].slice(0,3)}`;
  const weekHeaderMonth = `${MONTH_NAMES[weekDays[0].getMonth()]} ${weekDays[0].getFullYear()}`;

  // Helper to get day data
  const dayData = (date) => {
    const dStr = typeof date === "string" ? date : formatDateStr(date);
    const fR = reels.filter(r => r.brand==="franz" && r.date===dStr);
    const tR = reels.filter(r => r.brand==="tgc"   && r.date===dStr);
    const fRd = fR.some(r => r.status==="posted");
    const tRd = tR.some(r => r.status==="posted");
    const fRf = fR.some(r => r.status==="filmed");
    const tRf = tR.some(r => r.status==="filmed");
    return { fR, tR, fRd, tRd, fRf, tRf };
  };

  return (
    <div>
      {/* View Mode Toggle */}
      <div style={{ display:"flex", justifyContent:"center", marginBottom:14, gap:4 }}>
        <button onClick={()=>setViewMode("week")} style={{ padding:"8px 18px", borderRadius:8, border:`1px solid ${viewMode==="week"?TEXT:BORDER}`, background:viewMode==="week"?TEXT:"transparent", color:viewMode==="week"?BG:MUTED, fontSize:11, fontFamily:F_MONO, letterSpacing:"1px", cursor:"pointer", fontWeight:viewMode==="week"?700:400, minHeight:40 }}>WEEK</button>
        <button onClick={()=>setViewMode("month")} style={{ padding:"8px 18px", borderRadius:8, border:`1px solid ${viewMode==="month"?TEXT:BORDER}`, background:viewMode==="month"?TEXT:"transparent", color:viewMode==="month"?BG:MUTED, fontSize:11, fontFamily:F_MONO, letterSpacing:"1px", cursor:"pointer", fontWeight:viewMode==="month"?700:400, minHeight:40 }}>MONTH</button>
      </div>

      {viewMode === "week" ? (
        <>
          {/* Week View Header */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <button onClick={prevWeek} style={{ background:"none", border:`1px solid ${BORDER}`, borderRadius:8, width:44, height:44, cursor:"pointer", color:MUTED, fontSize:22, display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:m?11:13, color:MUTED, fontFamily:F_MONO, letterSpacing:"2px", marginBottom:2 }}>{weekHeaderMonth.toUpperCase()}</div>
              <div style={{ fontSize:m?16:18, fontWeight:700, color:TEXT }}>{weekRange}</div>
            </div>
            <button onClick={nextWeek} style={{ background:"none", border:`1px solid ${BORDER}`, borderRadius:8, width:44, height:44, cursor:"pointer", color:MUTED, fontSize:22, display:"flex", alignItems:"center", justifyContent:"center" }}>›</button>
          </div>

          {/* Week Day Cards — vertical list */}
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {weekDays.map((d) => {
              const dStr = formatDateStr(d);
              const data = dayData(dStr);
              const isToday = formatDateStr(now) === dStr;
              const dayName = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d.getDay()];
              const totalReels = data.fR.length + data.tR.length;

              return (
                <div key={dStr} onClick={() => onDayClick(d.getDate(), d.getFullYear(), d.getMonth())}
                  style={{ background:isToday?`${FRANZ}08`:CARD, border:`1px solid ${isToday?FRANZ:BORDER}`, borderRadius:12, padding:m?12:14, cursor:"pointer", transition:"all 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor="#999"}
                  onMouseLeave={e => e.currentTarget.style.borderColor=isToday?FRANZ:BORDER}>
                  {/* Day Header */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:totalReels>0?10:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ width:m?40:48, textAlign:"center" }}>
                        <div style={{ fontSize:9, color:isToday?FRANZ:MUTED, fontFamily:F_MONO, fontWeight:700, marginBottom:2 }}>{dayName.toUpperCase()}</div>
                        <div style={{ fontSize:m?20:24, fontWeight:700, color:isToday?FRANZ:TEXT, lineHeight:1 }}>{d.getDate()}</div>
                      </div>
                      {isToday && <div style={{ padding:"3px 8px", borderRadius:4, background:FRANZ, fontSize:9, color:"#fff", fontFamily:F_MONO, fontWeight:700, letterSpacing:"1px" }}>TODAY</div>}
                    </div>
                    {totalReels===0 && (
                      <span style={{ fontSize:10, color:MUTED, fontFamily:F_MONO, fontStyle:"italic" }}>No content planned</span>
                    )}
                  </div>

                  {/* Franz Row */}
                  {data.fR.length>0 && (
                    <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 10px", background:`${FRANZ}0A`, border:`1px solid ${FRANZ}33`, borderRadius:8, marginBottom:6 }}>
                      <div style={{ fontSize:9, color:FRANZ, fontFamily:F_MONO, fontWeight:700, width:m?42:50, flexShrink:0 }}>FRANZ</div>
                      {data.fR.length>0 ? (
                        <div style={{ display:"flex", alignItems:"center", gap:5, flex:1, minWidth:0 }}>
                          <div style={{ width:8, height:8, borderRadius:"50%", background:data.fRd?FRANZ:data.fRf?AMBER:`${FRANZ}55`, flexShrink:0 }}/>
                          <span style={{ fontSize:m?11:12, color:data.fRd?FRANZ:TEXT, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{data.fR[0]?.title}</span>
                          <span style={{ fontSize:9, color:data.fRd?FRANZ:MUTED, fontFamily:F_MONO, fontWeight:700, flexShrink:0 }}>{data.fRd?"✓":data.fRf?"FILMED":"PLANNED"}</span>
                        </div>
                      ) : <div style={{ flex:1, fontSize:10, color:MUTED, fontStyle:"italic" }}>No reel</div>}
                    </div>
                  )}

                  {/* TGC Row */}
                  {data.tR.length>0 && (
                    <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 10px", background:`${TGC}0A`, border:`1px solid ${TGC}33`, borderRadius:8 }}>
                      <div style={{ fontSize:9, color:TGC, fontFamily:F_MONO, fontWeight:700, width:m?42:50, flexShrink:0 }}>TGC</div>
                      {data.tR.length>0 ? (
                        <div style={{ display:"flex", alignItems:"center", gap:5, flex:1, minWidth:0 }}>
                          <div style={{ width:8, height:8, borderRadius:"50%", background:data.tRd?TGC:data.tRf?AMBER:`${TGC}55`, flexShrink:0 }}/>
                          <span style={{ fontSize:m?11:12, color:data.tRd?TGC:TEXT, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{data.tR[0]?.title}</span>
                          <span style={{ fontSize:9, color:data.tRd?TGC:MUTED, fontFamily:F_MONO, fontWeight:700, flexShrink:0 }}>{data.tRd?"✓":data.tRf?"FILMED":"PLANNED"}</span>
                        </div>
                      ) : <div style={{ flex:1, fontSize:10, color:MUTED, fontStyle:"italic" }}>No reel</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          {/* Month View Header */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <button onClick={pm} style={{ background:"none", border:`1px solid ${BORDER}`, borderRadius:8, width:44, height:44, cursor:"pointer", color:MUTED, fontSize:22, display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:m?16:18, fontWeight:700, color:TEXT }}>{MONTH_NAMES[vM]} {vY}</div>
              {!m && <div style={{ fontSize:10, color:MUTED, fontFamily:F_MONO }}>Click any day to see details</div>}
            </div>
            <button onClick={nm} style={{ background:"none", border:`1px solid ${BORDER}`, borderRadius:8, width:44, height:44, cursor:"pointer", color:MUTED, fontSize:22, display:"flex", alignItems:"center", justifyContent:"center" }}>›</button>
          </div>

          {/* Month grid */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:m?2:4, marginBottom:m?2:4 }}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} style={{ textAlign:"center", fontSize:m?8:10, color:MUTED, fontFamily:F_MONO, padding:"3px 0" }}>{d}</div>)}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:m?2:4 }}>
            {Array.from({length:sd}).map((_,i) => <div key={`e${i}`}/>)}
            {Array.from({length:days}).map((_,i) => {
              const day  = i+1, date = ds(day);
              const data = dayData(date);
              const isT  = day===tod;
              const fDot = data.fRd ? FRANZ : data.fRf ? AMBER : data.fR.length>0 ? `${FRANZ}44` : null;
              const tDot = data.tRd ? TGC   : data.tRf ? AMBER : data.tR.length>0 ? `${TGC}44`   : null;

              return (
                <div key={day} onClick={() => onDayClick(day, vY, vM)}
                  style={{ minHeight:m?60:78, borderRadius:m?6:8, padding:m?"4px":"6px 7px", background:isT?`${FRANZ}11`:CARD, border:`1px solid ${isT?FRANZ:BORDER}`, cursor:"pointer", transition:"all 0.15s" }}>
                  <div style={{ fontSize:m?11:12, fontWeight:isT?700:500, color:isT?FRANZ:TEXT, marginBottom:m?3:4, fontFamily:F_MONO }}>{day}</div>
                  {data.fR.length>0 && (
                    <div style={{ display:"flex", alignItems:"center", gap:3, marginBottom:m?3:4, padding:m?"2px 4px":"3px 5px", background:`${FRANZ}15`, borderRadius:m?4:6, border:`1px solid ${data.fRd?FRANZ:FRANZ+"44"}` }}>
                      {data.fR.length>0 && <div style={{ width:m?6:7, height:m?6:7, borderRadius:"50%", background:fDot, flexShrink:0 }}/>}
                      {!m && data.fR[0] && <div style={{ fontSize:9, color:data.fRd?FRANZ:MUTED, fontFamily:F_MONO, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>F · {data.fR[0]?.title.slice(0,12)}</div>}
                      {m && data.fR.length>0 && <span style={{ fontSize:7, color:data.fRd?FRANZ:MUTED, fontFamily:F_MONO, fontWeight:700 }}>F</span>}
                    </div>
                  )}
                  {data.tR.length>0 && (
                    <div style={{ display:"flex", alignItems:"center", gap:3, marginBottom:m?2:3, padding:m?"2px 4px":"3px 5px", background:`${TGC}15`, borderRadius:m?4:6, border:`1px solid ${data.tRd?TGC:TGC+"44"}` }}>
                      {data.tR.length>0 && <div style={{ width:m?6:7, height:m?6:7, borderRadius:"50%", background:tDot, flexShrink:0 }}/>}
                      {!m && data.tR[0] && <div style={{ fontSize:9, color:data.tRd?TGC:MUTED, fontFamily:F_MONO, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>T · {data.tR[0]?.title.slice(0,12)}</div>}
                      {m && data.tR.length>0 && <span style={{ fontSize:7, color:data.tRd?TGC:MUTED, fontFamily:F_MONO, fontWeight:700 }}>T</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Legend */}
      <div style={{ display:"flex", gap:m?10:16, marginTop:14, flexWrap:"wrap", justifyContent:"center" }}>
        {[{color:FRANZ,label:"Franz posted"},{color:AMBER,label:"Filmed"},{color:MUTED,label:"Planned"},{color:TGC,label:"TGC posted"}].map(l => (
          <div key={l.label} style={{ display:"flex", alignItems:"center", gap:5 }}>
            <div style={{ width:7, height:7, borderRadius:"50%", background:l.color }}/><span style={{ fontSize:9, color:MUTED, fontFamily:F_MONO }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// DAY MODAL
// ══════════════════════════════════════════════════════════════
export function DayModal({ day, year, month, reels, series, onClose, onOpenReel, onToggleReel, saving }) {
  const m   = useIsMobile();
  useSwipeBack(onClose);
  const ds  = `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
  const fR  = reels.filter(r => r.brand==="franz" && r.date===ds);
  const tR  = reels.filter(r => r.brand==="tgc"   && r.date===ds);

  const RR = ({ reel, brand }) => {
    const color = bc(brand);
    const sObj  = reel.type==="SERIES" ? series.find(s=>s.id===reel.series_id) : null;
    return (
      <div onClick={() => onOpenReel(reel, brand)} style={{ padding:"12px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:10, marginBottom:10, cursor:"pointer", transition:"all 0.15s" }}
        onMouseEnter={e => e.currentTarget.style.borderColor=color}
        onMouseLeave={e => e.currentTarget.style.borderColor=BORDER}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6, flexWrap:"wrap", gap:6 }}>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:color }}/>
            <span style={{ fontSize:11, color, fontFamily:F_MONO, fontWeight:700 }}>{brand==="franz"?"FRANZ":"TGC"}</span>
            {sObj && <span style={{ fontSize:10, color:sObj.color, fontFamily:F_MONO }}>{sObj.name} · Pt {reel.part}</span>}
          </div>
          <div onClick={e => e.stopPropagation()}>
            <StatusBadge status={reel.status} onClick={() => onToggleReel(reel.id, reel.status)} disabled={saving}/>
          </div>
        </div>
        <div style={{ fontSize:15, fontWeight:600, color:TEXT }}>{reel.title}</div>
        {reel.hook && <div style={{ fontSize:12, color:MUTED, fontStyle:"italic", marginTop:2 }}>"{reel.hook}"</div>}
        <div style={{ fontSize:9, color:MUTED, fontFamily:F_MONO, marginTop:6, letterSpacing:"1px" }}>TAP FOR DETAILS →</div>
      </div>
    );
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000, display:"flex", alignItems:m?"flex-end":"center", justifyContent:"center", padding:m?0:20 }}>
      <div style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:m?"16px 16px 0 0":"16px", padding:m?"calc(env(safe-area-inset-top) + 24px) 12px 36px":"28px", width:m?"100%":640, maxWidth:"100%", maxHeight:m?"94vh":"90vh", overflowY:"auto" }}>
        {m && <div style={{ width:40, height:4, background:BORDER, borderRadius:2, margin:"0 auto 14px" }}/>}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <div>
            <div style={{ fontSize:10, color:MUTED, fontFamily:F_MONO, letterSpacing:"2px", marginBottom:3 }}>{MONTH_NAMES[month].toUpperCase()} {year}</div>
            <div style={{ fontSize:m?17:22, fontWeight:700, color:TEXT }}>{new Date(`${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}T00:00:00`).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:MUTED, fontSize:22, cursor:"pointer", padding:12, minWidth:48, minHeight:48, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:8 }}>✕</button>
        </div>
        <div style={{ height:1, background:BORDER, marginBottom:18 }}/>

        {/* ═══ REELS SECTION ═══ */}
        {(fR.length > 0 || tR.length > 0) && (
          <div style={{ marginBottom:24 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
              <div style={{ height:2, flex:1, background:BORDER }}/>
              <div style={{ fontSize:11, color:MUTED, fontFamily:F_MONO, letterSpacing:"3px", fontWeight:700 }}>REELS</div>
              <div style={{ height:2, flex:1, background:BORDER }}/>
            </div>
            {fR.map(r => <RR key={r.id} reel={r} brand="franz"/>)}
            {tR.map(r => <RR key={r.id} reel={r} brand="tgc"/>)}
            {fR.length===0 && <div style={{ padding:"12px", border:`1px dashed ${BORDER}`, borderRadius:10, marginBottom:10, fontSize:11, color:MUTED, fontFamily:F_MONO, textAlign:"center" }}>No Franz reel planned</div>}
            {tR.length===0 && <div style={{ padding:"12px", border:`1px dashed ${BORDER}`, borderRadius:10, fontSize:11, color:MUTED, fontFamily:F_MONO, textAlign:"center" }}>No TGC reel planned</div>}
          </div>
        )}

        {fR.length===0&&tR.length===0&&<div style={{ textAlign:"center", padding:40, color:MUTED, fontFamily:F_MONO }}>No content for this day.</div>}

        <div style={{ display:"flex", justifyContent:"flex-end", marginTop:14 }}><Btn onClick={onClose} accent={MUTED}>CLOSE</Btn></div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ══════════════════════════════════════════════════════════════
function BriefingTab() {
  const m = useIsMobile();

  const Section = ({ title, color, children }) => (
    <div style={{ background:CARD, border:`1px solid ${BORDER}`, borderLeft:`4px solid ${color}`, borderRadius:14, padding:m?"18px 16px":"24px 28px", marginBottom:14, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
      <div style={{ fontSize:11, color, fontFamily:F_MONO, letterSpacing:"2px", fontWeight:700, marginBottom:12 }}>{title.toUpperCase()}</div>
      {children}
    </div>
  );

  const P = ({ children, bold }) => (
    <p style={{ fontSize:14, color:TEXT, lineHeight:1.6, marginBottom:10, fontWeight: bold ? 600 : 400 }}>{children}</p>
  );

  const Bullet = ({ children }) => (
    <li style={{ fontSize:14, color:TEXT, lineHeight:1.7, marginBottom:6, paddingLeft:6 }}>{children}</li>
  );

  const Code = ({ children }) => (
    <span style={{ background:SOFT, padding:"2px 7px", borderRadius:4, fontFamily:F_MONO, fontSize:12, color:TEXT }}>{children}</span>
  );

  return (
    <div style={{ maxWidth:900, margin:"0 auto" }}>

      {/* Welcome Header */}
      <div style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:14, padding:m?"20px 16px":"32px", marginBottom:16, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:11, color:MUTED, fontFamily:F_MONO, letterSpacing:"3px", marginBottom:6 }}>VIDEOGRAPHER ONBOARDING</div>
        <div style={{ fontSize:m?22:30, fontWeight:700, color:TEXT, marginBottom:8 }}>Welcome to the Team</div>
        <p style={{ fontSize:14, color:MUTED, lineHeight:1.6, marginBottom:0 }}>
          This is your everything-you-need-to-know guide. Read it once, then come back whenever you need a refresher.
          The dashboard is your daily source of truth — what to film, when to film, and where it lives.
        </p>
      </div>

      {/* Brands */}
      <Section title="The Two Brands" color={FRANZ}>
        <div style={{ display:"grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap:12 }}>
          <div style={{ padding:"14px 16px", background:`${FRANZ}0A`, border:`1px solid ${FRANZ}33`, borderRadius:10 }}>
            <div style={{ fontSize:11, color:FRANZ, fontFamily:F_MONO, fontWeight:700, letterSpacing:"2px", marginBottom:6 }}>FRANZ</div>
            <P><b>Cinnamon roll &amp; specialty coffee/matcha shop.</b></P>
            <P><b>USP:</b> free oat milk (rare in Bali).</P>
            <P><b>Vibe:</b> girly, aesthetic, premium, feminine. Soft pinks, creams, light wood.</P>
            <P><b>Hero products:</b> 6 iced matchas, 6 Fine Selection iced coffees, cinnamon rolls.</P>
          </div>
          <div style={{ padding:"14px 16px", background:`${TGC}0A`, border:`1px solid ${TGC}33`, borderRadius:10 }}>
            <div style={{ fontSize:11, color:TGC, fontFamily:F_MONO, fontWeight:700, letterSpacing:"2px", marginBottom:6 }}>THE GREEN COLLECTIVE</div>
            <P><b>Premium health takeaway store.</b></P>
            <P><b>USP:</b> clean, transparent, California-grade wellness.</P>
            <P><b>Vibe:</b> clean, organic, premium. Raw concrete, kraft, glass, palm leaves.</P>
            <P><b>Hero products:</b> 9 infused waters, 8 whey protein bars, juices, electrolytes.</P>
          </div>
        </div>
      </Section>

      {/* Target Audience */}
      <Section title="Target Audience" color={BUILD}>
        <P>Age 20–35. Expats, digital nomads, surfer-influencer-adjacent crowd in Pererenan and Canggu.</P>
        <P>International — mostly Australia, USA, Europe, Russia.</P>
        <P>They live on TikTok and Reels, scroll muted, decide in 1.5 seconds.</P>
        <P bold>What they want:</P>
        <ul style={{ marginTop:0, marginBottom:12, paddingLeft:24 }}>
          <Bullet>Anti-aspirational cool — real ingredients, lived-in spaces, confident understatement</Bullet>
          <Bullet>Aesthetic close-ups, hands-only POV, ASMR product moments</Bullet>
          <Bullet>Stories where they can see who's behind the brand, not just polished marketing</Bullet>
        </ul>
        <P bold>What they scroll past:</P>
        <ul style={{ marginTop:0, paddingLeft:24 }}>
          <Bullet>Corporate language, forced smiles, posed customer shots</Bullet>
          <Bullet>Generic Bali tourism cues (beach sunsets, scooters in traffic)</Bullet>
          <Bullet>Over-saturated green grading, tropical filters</Bullet>
        </ul>
      </Section>

      {/* How We Shoot */}
      <Section title="How We Shoot — Core Rules" color={FRANZ}>
        <ul style={{ marginTop:0, paddingLeft:24 }}>
          <Bullet><b>You stay behind the camera.</b> No selfie content. No talking-head videos. Story is told through hands, products, spaces.</Bullet>
          <Bullet><b>POV and ASMR are the spine.</b> Phone-in-hand POV walking shots. Macro close-ups of pours, drips, glaze, condensation. ASMR mic on for every product moment.</Bullet>
          <Bullet><b>Hands only.</b> When people appear, only hands are visible. Faces only in planned Day-In-The-Life episodes.</Bullet>
          <Bullet><b>Vertical 9:16 or 4:5.</b> Shot on iPhone 15 Pro or newer. Always vertical.</Bullet>
          <Bullet><b>Light = morning + golden hour.</b> Best windows: 7–9am and 5–7pm Bali time. Avoid harsh midday.</Bullet>
          <Bullet><b>Hooks in 1.5 seconds.</b> Every reel must grab in the first beat — strong visual or text overlay.</Bullet>
          <Bullet><b>Trust the visuals.</b> Captions are short, lowercase, often a single line. Never explain what's already shown.</Bullet>
        </ul>
      </Section>

      {/* Posting Frequency */}
      <Section title="Posting Frequency" color={TGC}>
        <P bold>Daily output (per brand):</P>
        <ul style={{ marginTop:0, marginBottom:12, paddingLeft:24 }}>
          <Bullet><b>1 Reel per day</b> — planned in advance via this dashboard, with hook + shot list + caption + audio direction</Bullet>
        </ul>
        <P bold>Total monthly: 62 Reels (31 Franz + 31 TGC)</P>
        <P bold>Best posting windows:</P>
        <ul style={{ marginTop:0, paddingLeft:24 }}>
          <Bullet><b>11:00–13:00 Bali time</b> — peak afternoon for AU/EU lunch overlap</Bullet>
          <Bullet><b>17:00–18:00 Bali time</b> — golden hour drop, peak USA evening</Bullet>
        </ul>
      </Section>

      {/* Daily Rhythm */}
      <Section title="Daily Rhythm — Three Shoot Windows" color={BUILD}>
        <div style={{ marginBottom:12, padding:"12px 14px", background:SOFT, borderRadius:8 }}>
          <div style={{ fontSize:12, color:TEXT, fontWeight:700, marginBottom:4 }}>🌅 Morning Batch — 7–9am</div>
          <P>Opening shots, bakery prep, fresh roll trays, espresso machine warm-up, morning light through rice field windows. Best window for any morning-light reel.</P>
        </div>
        <div style={{ marginBottom:12, padding:"12px 14px", background:SOFT, borderRadius:8 }}>
          <div style={{ fontSize:12, color:TEXT, fontWeight:700, marginBottom:4 }}>☀️ Midday Batch — 11:30am–12:30pm</div>
          <P>Drink hero shots, cinnamon roll pulls, fridge restocks, customer hand moments. This is where the day's main reel usually comes from.</P>
        </div>
        <div style={{ padding:"12px 14px", background:SOFT, borderRadius:8 }}>
          <div style={{ fontSize:12, color:TEXT, fontWeight:700, marginBottom:4 }}>🌇 Golden Hour Batch — 5–7pm</div>
          <P>Atmosphere, rice fields, bench oasis, customer back-of-head moments, sunset reels. Best window for any golden-hour reel.</P>
        </div>
      </Section>

      {/* File Naming Convention */}
      <Section title="File Naming Convention" color={AMBER}>
        <P>Every file follows this format:</P>
        <div style={{ padding:"14px 16px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:8, marginBottom:12 }}>
          <div style={{ fontSize:13, fontFamily:F_MONO, color:TEXT, fontWeight:700 }}>YYYY-MM-DD_BRAND_TYPE_Title-With-Dashes</div>
        </div>
        <P bold>Examples:</P>
        <ul style={{ marginTop:0, marginBottom:12, paddingLeft:24, listStyleType:"none" }}>
          <Bullet><Code>2026-05-04_FRANZ_REEL_Mango-Matcha-Appreciation</Code></Bullet>
          <Bullet><Code>2026-05-08_FRANZ_STREET_Bauarbeiter-Cinnamon-Rolls</Code></Bullet>
          <Bullet><Code>2026-05-14_FRANZ_DITL_The-Baker</Code></Bullet>
          <Bullet><Code>2026-05-23_TGC_SERIES_The-NMAX-Build-Reveal</Code></Bullet>
        </ul>
        <P bold>Type codes:</P>
        <ul style={{ marginTop:0, marginBottom:12, paddingLeft:24 }}>
          <Bullet><Code>REEL</Code> — Standalone Reels</Bullet>
          <Bullet><Code>SERIES</Code> — Series Episodes (Perfect Roll, NMAX Build, etc.)</Bullet>
          <Bullet><Code>STREET</Code> — Street Reactions (Tukang, Surfer, Pilates Girls, etc.)</Bullet>
          <Bullet><Code>DITL</Code> — Day In The Life Episodes</Bullet>
        </ul>
        <div style={{ padding:"12px 14px", background:`${AMBER}15`, border:`1px solid ${AMBER}33`, borderRadius:8, marginTop:8 }}>
          <P bold>💡 Pro Tip</P>
          <P>Every reel in the dashboard already has its file name pre-generated. Open the reel detail and tap the file name to copy it. Then rename your file before uploading to Drive.</P>
        </div>
      </Section>

      {/* Drive Folder Structure */}
      <Section title="Google Drive Folder Structure" color={TGC}>
        <P>All videos go into Google Drive. Folder structure is set up by the team.</P>
        <div style={{ padding:"16px 18px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:10, fontFamily:F_MONO, fontSize:13, color:TEXT, lineHeight:1.7 }}>
          📁 Content Archive<br/>
          &nbsp;&nbsp;└── 📁 2026-05-May<br/>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;├── 📁 Franz<br/>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;│&nbsp;&nbsp;&nbsp;├── 📁 RAW<br/>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;│&nbsp;&nbsp;&nbsp;└── 📁 FINAL<br/>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└── 📁 TGC<br/>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;├── 📁 RAW<br/>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└── 📁 FINAL
        </div>
        <ul style={{ marginTop:14, paddingLeft:24 }}>
          <Bullet><b>RAW folder:</b> All original/unedited footage. Backup material for later compilations.</Bullet>
          <Bullet><b>FINAL folder:</b> The final exported reel ready to post. This is what goes live on Instagram/TikTok.</Bullet>
        </ul>
      </Section>

      {/* Workflow */}
      <Section title="Workflow — From Idea to Posted" color={FRANZ}>
        <ol style={{ marginTop:0, paddingLeft:24, fontSize:14, color:TEXT, lineHeight:1.7 }}>
          <li style={{ marginBottom:8 }}><b>Open the Dashboard daily</b> — check what's planned for today</li>
          <li style={{ marginBottom:8 }}><b>Read the reel brief</b> — hook, what to film, vibe, audio direction, text overlays</li>
          <li style={{ marginBottom:8 }}><b>Copy the file name</b> from the reel detail (tap to copy)</li>
          <li style={{ marginBottom:8 }}><b>Shoot the footage</b> following the brief — bias toward shooting more than needed</li>
          <li style={{ marginBottom:8 }}><b>Upload to RAW folder</b> in the right brand folder</li>
          <li style={{ marginBottom:8 }}><b>Edit the reel</b> — add text overlays, audio, color grading</li>
          <li style={{ marginBottom:8 }}><b>Export and rename</b> with the dashboard file name</li>
          <li style={{ marginBottom:8 }}><b>Upload final to FINAL folder</b> in the right brand folder</li>
          <li style={{ marginBottom:8 }}><b>Copy the Drive link</b> of the final video</li>
          <li style={{ marginBottom:8 }}><b>Paste link in Dashboard</b> — open the reel detail, paste in "Drive Link" field. Status auto-updates to "Filmed"</li>
          <li><b>After posting on Instagram/TikTok</b> — set status to "Posted" in the dashboard</li>
        </ol>
      </Section>

      {/* Series Overview */}
      <Section title="Active Series" color={BUILD}>
        <P>Series episodes post when real progress happens — not on fixed dates. Between episodes, standalone reels fill the schedule.</P>
        <ul style={{ marginTop:0, paddingLeft:24 }}>
          <Bullet><b>The Perfect Roll</b> (Franz, 4 parts) — cinnamon roll story arc</Bullet>
          <Bullet><b>What's In Your Drink</b> (Both, 4 parts) — origin/transparency for matcha, oat milk, coffee, infused water</Bullet>
          <Bullet><b>DITL · The Baker</b> (Franz, 1) — day in the life of the baker</Bullet>
          <Bullet><b>DITL · Cory the Barista</b> (Franz, 1) — day in the life of the barista</Bullet>
          <Bullet><b>DITL · Faiz</b> (TGC, 1) — day in the life of operations manager</Bullet>
          <Bullet><b>The TGC Sign</b> (TGC, 2) — sign build retrospective + reveal</Bullet>
          <Bullet><b>The NMAX Build</b> (TGC, 3) — delivery scooter custom build</Bullet>
          <Bullet><b>Building TGC From Zero</b> (TGC, 3) — pre-launch BTS, launch day, one week in</Bullet>
          <Bullet><b>Find The Billboard</b> (TGC, 2) — quest to install billboard</Bullet>
          <Bullet><b>Photo Booth Hunt</b> (TGC, 1+) — photo booth restoration</Bullet>
          <Bullet><b>Street Reactions</b> (Both, 5+) — give products to Tukang, Surfer, Banjar Neighbours, Pilates Girls, Gym Crowd. Film reactions.</Bullet>
        </ul>
      </Section>

      {/* What to Film vs Avoid */}
      <Section title="Film vs Don't Film" color={AMBER}>
        <div style={{ display:"grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap:12 }}>
          <div style={{ padding:"14px 16px", background:`${TGC}0A`, border:`1px solid ${TGC}33`, borderRadius:10 }}>
            <div style={{ fontSize:13, color:TGC, fontWeight:700, marginBottom:8 }}>✓ FILM</div>
            <ul style={{ margin:0, paddingLeft:20, fontSize:13, color:TEXT, lineHeight:1.7 }}>
              <li>Drink pours, layered builds, condensation, ice cracks</li>
              <li>Cinnamon roll pulls, glaze drips, bake textures</li>
              <li>Fridge restocks, ingredient flat lays</li>
              <li>Hands placing, pouring, opening, closing</li>
              <li>Rice fields through windows, palm leaf shadows</li>
              <li>POV walks at golden hour</li>
              <li>Bench oasis at sunset</li>
              <li>NMAX scooter, key turns</li>
              <li>Production kitchen process</li>
            </ul>
          </div>
          <div style={{ padding:"14px 16px", background:`${FRANZ}0A`, border:`1px solid ${FRANZ}33`, borderRadius:10 }}>
            <div style={{ fontSize:13, color:FRANZ, fontWeight:700, marginBottom:8 }}>✗ AVOID</div>
            <ul style={{ margin:0, paddingLeft:20, fontSize:13, color:TEXT, lineHeight:1.7 }}>
              <li>Full faces (except planned DITL)</li>
              <li>Selfie-style talking videos</li>
              <li>Forced smiles, posed shots</li>
              <li>Generic Bali tourism cues</li>
              <li>Over-saturated tropical filters</li>
              <li>Loud transitions, gimmicky effects</li>
              <li>Long-form explanation videos</li>
              <li>Hashtag-spam content</li>
              <li>Anything that looks like a corporate ad</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* Voice & Tone */}
      <Section title="Voice & Tone Cheat-Sheet" color={FRANZ}>
        <P>Lowercase. One line. Trust the visual. Confident understatement.</P>
        <div style={{ display:"grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap:12, marginTop:8 }}>
          <div style={{ padding:"12px 14px", background:`${TGC}0A`, border:`1px solid ${TGC}33`, borderRadius:10 }}>
            <div style={{ fontSize:11, color:TGC, fontWeight:700, marginBottom:6, fontFamily:F_MONO, letterSpacing:"1px" }}>DO</div>
            <ul style={{ margin:0, paddingLeft:18, fontSize:13, color:TEXT, lineHeight:1.6 }}>
              <li>"oat milk is on us, always"</li>
              <li>"less sweet. more grown-up."</li>
              <li>"she opens monday. quietly."</li>
              <li>"infused water but the kind your skin notices"</li>
            </ul>
          </div>
          <div style={{ padding:"12px 14px", background:`${FRANZ}0A`, border:`1px solid ${FRANZ}33`, borderRadius:10 }}>
            <div style={{ fontSize:11, color:FRANZ, fontWeight:700, marginBottom:6, fontFamily:F_MONO, letterSpacing:"1px" }}>DON'T</div>
            <ul style={{ margin:0, paddingLeft:18, fontSize:13, color:TEXT, lineHeight:1.6 }}>
              <li>"We're proud to offer complimentary plant-based milk alternatives"</li>
              <li>"A balanced, sophisticated flavour profile"</li>
              <li>"Grand opening this Monday — visit us!"</li>
              <li>"Hydration with benefits!"</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* Locations */}
      <Section title="Best Filming Locations" color={TGC}>
        <ul style={{ marginTop:0, paddingLeft:24 }}>
          <Bullet><b>Floor-to-ceiling windows</b> — rice fields visible, perfect for morning light reels</Bullet>
          <Bullet><b>Green Oasis bench</b> outside, surrounded by palm trees — golden hour gold</Bullet>
          <Bullet><b>Production Kitchen</b> — for BTS, juice production, baking, bottling</Bullet>
          <Bullet><b>Front entrance</b> with palm shadows — for door/open sign reels</Bullet>
          <Bullet><b>Rice fields front and back of stores</b> — atmospheric establishing shots</Bullet>
        </ul>
      </Section>

      {/* Final Note */}
      <div style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:14, padding:m?"18px 16px":"24px 28px", marginBottom:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:11, color:MUTED, fontFamily:F_MONO, letterSpacing:"2px", marginBottom:8 }}>FIRST WEEK NOTE</div>
        <P>First week is observation and shooting alongside Cory. By week two you'll be running shoots solo. Don't worry about getting everything right immediately. Bias toward shooting more than we need. The dashboard is the source of truth — when in doubt, check it.</P>
        <P bold>We meet weekly to review what worked and adjust. Welcome to the team.</P>
      </div>

    </div>
  );
}

function Dashboard({ user, role = "creator", profiles = {} }) {
  const m = useIsMobile();

  const [reels,   setReels]   = useState([]);
  const [series,  setSeries]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);

  const [tab,   setTab]   = useState("overview");
  const [brand, setBrand] = useState("all");   // global brand filter: all | franz | tgc
  // Tabs that create rows need a concrete brand.
  const wBrand = brand === "all" ? "franz" : brand;
  const brandReels = brand === "all" ? reels : reels.filter(r => r.brand === brand);

  const [showAddReel,  setShowAddReel]  = useState(false);
  const [newReel,      setNewReel]      = useState({ brand:"franz", date:"", title:"", caption:"", hook:"", description:"", format:"", notes:"", type:"REEL", series:"", part:"", assignee:"", pillar:"", est_length:"", reference_link:"" });
  const [detailReel,   setDetailReel]   = useState(null);
  const [detailAnalytics, setDetailAnalytics] = useState(null);
  const [calendarDay,  setCalendarDay]  = useState(null);

  // Lazy-load analytics only when a reel detail opens (kept out of the main load).
  useEffect(() => {
    if (!detailReel) { setDetailAnalytics(null); return; }
    let active = true;
    fetchAnalyticsForReel(detailReel.reel.id)
      .then(a => { if (active) setDetailAnalytics(a); })
      .catch(() => {});
    return () => { active = false; };
  }, [detailReel]);
  const [showBulk,     setShowBulk]     = useState(false);
  const [bulkFile,     setBulkFile]     = useState(null);
  const [bulkPreview,  setBulkPreview]  = useState(null);
  const [bulkBrand,    setBulkBrand]    = useState("franz");
  const [showPwd,      setShowPwd]      = useState(false);
  const [newPwd,       setNewPwd]       = useState("");
  const [pwdMsg,       setPwdMsg]       = useState(null);
  const [anMap,        setAnMap]        = useState({});
  const [anLoading,    setAnLoading]    = useState(false);

  // Load all metrics when the Analytics tab opens (small table; fresh each visit).
  useEffect(() => {
    if (tab !== "analytics") return;
    let active = true;
    setAnLoading(true);
    fetchAllAnalytics()
      .then(rows => { if (!active) return; const mp = {}; rows.forEach(r => { mp[r.reel_id] = r; }); setAnMap(mp); })
      .catch(() => {})
      .finally(() => { if (active) setAnLoading(false); });
    return () => { active = false; };
  }, [tab]);

  const loadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [s, r] = await Promise.all([fetchSeries(), fetchReels()]);
      setSeries(s||[]); setReels(r||[]);
    } catch (e) { setError("Connection failed: " + e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Status toggle — cycles forward planned→filmed→posted→planned ──
  const handleToggleStatus = async (id, currentStatus) => {
    const ns = currentStatus === "posted" ? "planned" : nextStatus(currentStatus);
    setReels(prev => prev.map(r => r.id===id ? {...r, status:ns} : r));
    try {
      await updateReelStatus(id, ns);
    } catch (e) {
      setReels(prev => prev.map(r => r.id===id ? {...r, status:currentStatus} : r));
      setError("Status update failed: " + e.message);
    }
  };
  // ── Set status directly to a specific value (auto-stamps posted_at) ──
  const handleSetStatus = async (id, newStatus) => {
    const oldStatus = reels.find(r => r.id === id)?.status;
    if (oldStatus === newStatus) return;
    const postedAt = newStatus === "posted" ? new Date().toISOString() : null;
    // Optimistic UI update
    setReels(prevList => prevList.map(r => r.id===id ? {...r, status:newStatus, posted_at:postedAt || r.posted_at} : r));
    try {
      await updateReelStatus(id, newStatus);
      if (newStatus === "posted" && postedAt) {
        await updateReelPostedAt(id, postedAt);
      }
    } catch (e) {
      // Revert on error
      setReels(prevList => prevList.map(r => r.id===id ? {...r, status:oldStatus} : r));
      setError("Status update failed: " + e.message);
    }
  };

  // ── Update Drive Link (auto-sets status to filmed if planned) ──
  const handleUpdateDriveLink = async (id, link) => {
    const reel = reels.find(r => r.id === id);
    const wasJustPlanned = reel?.status === "planned" && link;
    setReels(prevList => prevList.map(r => r.id===id ? {...r, drive_link:link, status: wasJustPlanned ? "filmed" : r.status} : r));
    try {
      await updateReelDriveLink(id, link);
      if (wasJustPlanned) {
        await updateReelStatus(id, "filmed");
      }
    } catch (e) {
      setReels(prevList => prevList.map(r => r.id===id ? {...r, drive_link:reel?.drive_link} : r));
      setError("Drive link update failed: " + e.message);
    }
  };

  const handleAddReel = async () => {
    if (!newReel.title||!newReel.date) return;
    setSaving(true); setError(null);
    try {
      const created = await addReel(newReel);
      setReels(prev => [...prev, created].sort((a,b)=>a.date.localeCompare(b.date)));
      setNewReel({ brand:"franz", date:"", title:"", caption:"", hook:"", description:"", format:"", notes:"", type:"REEL", series:"", part:"", assignee:"", pillar:"", est_length:"", reference_link:"" });
      setShowAddReel(false);
    } catch(e){setError(e.message);}finally{setSaving(false);}
  };

  const handleDeleteReel = async (id) => {
    setSaving(true);
    try { await deleteReel(id); setReels(prev=>prev.filter(r=>r.id!==id)); }
    catch(e){setError(e.message);}finally{setSaving(false);}
  };

  // ── Series admin (admin-only; RLS also enforces it) ──
  const handleAddSeries = async (s) => {
    setError(null);
    try {
      const created = await addSeries(s);
      setSeries(prev => [...prev, created].sort((a,b)=>a.name.localeCompare(b.name)));
    } catch(e){ setError("Add series failed: " + e.message); }
  };
  const handleDeleteSeries = async (id) => {
    const prevList = series;
    setSeries(p => p.filter(s => s.id !== id));
    try { await deleteSeries(id); }
    catch(e){ setSeries(prevList); setError("Delete series failed: " + e.message); }
  };

  // Edit a reel's content (title/hook/caption/…) — any logged-in user.
  // Returns {ok:true} / {ok:false,error} so the caller can keep an edit form
  // open instead of closing it as if the save had succeeded.
  const handleUpdateReel = async (id, fields) => {
    setSaving(true); setError(null);
    try {
      const updated = await updateReel(id, fields);
      setReels(prev => prev.map(r => r.id===id ? {...r, ...updated} : r));
      return { ok: true };
    } catch(e){
      setError("Update failed: " + e.message);
      return { ok: false, error: e.message };
    }
    finally { setSaving(false); }
  };

  // Change own password.
  const handleChangePassword = async () => {
    if (newPwd.length < 6) { setPwdMsg("Password must be at least 6 characters."); return; }
    setSaving(true); setPwdMsg(null);
    try {
      await changePassword(newPwd);
      setPwdMsg("Password updated ✓"); setNewPwd("");
      setTimeout(() => { setShowPwd(false); setPwdMsg(null); }, 1200);
    } catch(e){ setPwdMsg(e.message); }
    finally { setSaving(false); }
  };

  const handleSaveAnalytics = async (reelId, vals) => {
    // All fields blank? Don't write a 0-view row — it would count as "tracked"
    // and always win the lowest-views highlight.
    if (!["views","likes","comments","shares","saves"].some(k => String(vals[k] ?? "").trim() !== "")) {
      setDetailReel(null); return;
    }
    setSaving(true);
    try {
      const row = await saveAnalytics(reelId, vals);
      // Keep the Analytics page in sync — it renders from anMap, which is
      // otherwise only filled when the tab is (re)opened.
      if (row) { setAnMap(prev => ({ ...prev, [reelId]: row })); setDetailAnalytics(row); }
      setDetailReel(null);
    }
    catch(e){setError("Saving metrics failed: " + e.message);}finally{setSaving(false);}
  };

  const handleBulkFile = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBulkFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setBulkPreview(normalizeBulkRows(ev.target.result, bulkBrand));
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    if (!bulkPreview || bulkPreview.reels.length === 0) return;
    setSaving(true); setError(null);
    try {
      await bulkImportReels(bulkPreview.reels);
      await loadAll();
      setShowBulk(false); setBulkFile(null); setBulkPreview(null);
    } catch(e){setError(e.message);}finally{setSaving(false);}
  };

  // Stats
  const totalPosted = reels.filter(r=>r.status==="posted").length;
  const totalFilmed = reels.filter(r=>r.status==="filmed").length;
  const totalPlanned= reels.filter(r=>r.status==="planned").length;

  const seriesPct = (sid) => {
    const s=series.find(x=>x.id===sid); if(!s) return 0;
    const bs=s.brand==="Both"?["franz","tgc"]:[s.brand.toLowerCase()];
    let done=0;
    bs.forEach(b=>reels.filter(r=>r.brand===b&&r.type==="SERIES"&&r.series_id===sid&&r.status==="posted").forEach(()=>done++));
    return Math.round((done/s.parts)*100);
  };

  const todayLong = new Date().toLocaleDateString("en-GB", {
    weekday:"long", day:"numeric", month:"long", year:"numeric",
  }) + " · Bali";

  const TABS = [
    ["overview",  "🏠", "Overview",     "Home"],
    ["plan",      "🎬", "Content plan", "Plan"],
    ["calendar",  "🗓", "Calendar",     "Cal"],
    ["series",    "🎞", "Series",       "Series"],
    ["analytics", "📊", "Analytics",    "Stats"],
    ["briefing",  "📖", "Briefing",     "Brief"],
  ];

  return (
    <div style={{ minHeight:"100dvh", background:BG, fontFamily:F_BODY, color:TEXT, paddingBottom:m?72:0 }}>

      {/* ── Modals ── */}
      {detailReel && (
        <ReelDetail 
          reel={reels.find(r => r.id === detailReel.reel.id) || detailReel.reel} 
          brand={detailReel.brand} series={series}
          onClose={()=>setDetailReel(null)}
          onToggleStatus={handleToggleStatus}
          onSetStatus={handleSetStatus}
          onUpdateDriveLink={handleUpdateDriveLink}
          onUpdateReel={handleUpdateReel}
          role={role}
          saving={saving}
          analytics={detailAnalytics}
          onSaveAnalytics={handleSaveAnalytics}/>
      )}
      {calendarDay && (
        <DayModal day={calendarDay.day} year={calendarDay.year} month={calendarDay.month}
          reels={reels} series={series}
          onClose={()=>setCalendarDay(null)}
          onOpenReel={(reel,brand)=>{setCalendarDay(null);setDetailReel({reel,brand});}}
          onToggleReel={handleToggleStatus}
          saving={saving}/>
      )}
      {showAddReel && (
        <Modal title="Add New Reel" onClose={()=>setShowAddReel(false)} onSave={handleAddReel} saving={saving} wide>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ display:"flex", gap:8 }}>{["franz","tgc"].map(b=><BrandToggle key={b} brand={b} active={newReel.brand===b} onClick={()=>setNewReel(p=>({...p,brand:b}))} compact={m}/>)}</div>
            <div style={{ display:"flex", flexDirection:m?"column":"row", gap:8 }}>
              <div style={{ flex:1 }}><DatePicker value={newReel.date} onChange={v=>setNewReel(p=>({...p,date:v}))} accentColor={bc(newReel.brand)}/></div>
              <select value={newReel.type} onChange={e=>setNewReel(p=>({...p,type:e.target.value}))} style={{ flex:1, padding:"10px 12px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:6, color:TEXT, fontSize:14, fontFamily:F_MONO, minHeight:44 }}>
                <option value="REEL">Standalone Reel</option><option value="SERIES">Part of a Series</option>
              </select>
            </div>
            {newReel.type==="SERIES" && (
              <div style={{ display:"flex", flexDirection:m?"column":"row", gap:8 }}>
                <select value={newReel.series} onChange={e=>setNewReel(p=>({...p,series:e.target.value}))} style={{ flex:1, padding:"10px 12px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:6, color:TEXT, fontSize:14, fontFamily:F_MONO, minHeight:44 }}>
                  <option value="">Select series...</option>
                  {series.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <Input value={newReel.part} onChange={v=>setNewReel(p=>({...p,part:v}))} placeholder="Part #" style={{ width:m?"100%":80 }}/>
              </div>
            )}
            <Input value={newReel.title}       onChange={v=>setNewReel(p=>({...p,title:v}))}       placeholder="Title *"/>
            <Input value={newReel.caption}     onChange={v=>setNewReel(p=>({...p,caption:v}))}     placeholder="Caption"/>
            <Input value={newReel.hook}        onChange={v=>setNewReel(p=>({...p,hook:v}))}        placeholder="Hook — First 2 Seconds"/>
            <textarea value={newReel.description} onChange={e=>setNewReel(p=>({...p,description:e.target.value}))} placeholder="What to film"
              style={{ padding:"10px 12px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:6, color:TEXT, fontSize:14, fontFamily:F_MONO, minHeight:80, resize:"vertical" }}/>
            <Input value={newReel.format} onChange={v=>setNewReel(p=>({...p,format:v}))} placeholder="Format & Style"/>
            <div style={{ display:"flex", flexDirection:m?"column":"row", gap:8 }}>
              <div style={{ flex:1 }}>
                <input list="assigneeList" value={newReel.assignee} onChange={e=>setNewReel(p=>({...p,assignee:e.target.value}))} placeholder="Owner (Ando / Yugo)"
                  style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", background:SOFT, border:`1px solid ${BORDER}`, borderRadius:6, color:TEXT, fontSize:14, minHeight:44 }}/>
                <datalist id="assigneeList"><option value="Ando"/><option value="Yugo"/></datalist>
              </div>
              <Input value={newReel.pillar} onChange={v=>setNewReel(p=>({...p,pillar:v}))} placeholder="Pillar (Process / BTS / USP …)" style={{ flex:1 }}/>
              <Input value={newReel.est_length} onChange={v=>setNewReel(p=>({...p,est_length:v}))} placeholder="Est. length (e.g. 20s)" style={{ flex:m?1:0.6 }}/>
            </div>
            <Input value={newReel.reference_link} onChange={v=>setNewReel(p=>({...p,reference_link:v}))} placeholder="Reference link (optional)"/>
            <Input value={newReel.notes}  onChange={v=>setNewReel(p=>({...p,notes:v}))}  placeholder="Director's Note (optional)"/>
          </div>
        </Modal>
      )}
      {showPwd && (
        <Modal title="Change password" onClose={()=>{setShowPwd(false);setNewPwd("");setPwdMsg(null);}} onSave={handleChangePassword} saving={saving}>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ fontSize:13, color:MUTED }}>Set a new password for your account ({user?.email}).</div>
            <input type="password" placeholder="New password (min. 6 characters)" value={newPwd} onChange={e=>setNewPwd(e.target.value)} autoComplete="new-password"
              style={{ padding:"11px 13px", borderRadius:8, border:`1px solid ${BORDER}`, fontSize:14, boxSizing:"border-box" }}/>
            {pwdMsg && <div style={{ fontSize:13, color: pwdMsg.includes("✓")?GREEN:"#DC2626" }}>{pwdMsg}</div>}
          </div>
        </Modal>
      )}

      {showBulk && (
        <Modal title="Bulk Import — CSV" onClose={()=>{setShowBulk(false);setBulkFile(null);setBulkPreview(null);}} onSave={(bulkPreview && bulkPreview.reels.length)?handleBulkImport:null} saving={saving} wide>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div style={{ padding:"12px 16px", background:`${FRANZ}0F`, border:`1px solid ${FRANZ}33`, borderRadius:10 }}>
              <div style={{ fontSize:11, color:FRANZ, fontFamily:F_MONO, fontWeight:700, marginBottom:6 }}>CSV FORMAT</div>
              <div style={{ fontSize:13, color:TEXT, lineHeight:1.7 }}>Upload the monthly plan as CSV — the Google-Sheet export works directly (columns like <b>Date, Owner, Pillar, Format, Working Title, Concept, Hook, Link, Note, Approved by</b>). Dates like <b>26.08.26</b> are understood. Rows without a brand become <b>franz</b>.</div>
              <button onClick={()=>{
                const tpl = [
                  "type,brand,date,owner,pillar,format,working title,concept,hook (first 1-2s),link,caption,est. length,notes,series,part",
                  'REEL,franz,26.09.26,Ando,Process,Voice-over build,Matcha #4 build,"Top-down build of the drink, name it, price it, sell it",Whisk hitting the bowl,,matcha of the week.,20-30s,Audio: trending sound,,',
                  'SERIES,franz,27.09.26,Yugo,BTS,Series 1/3,Kitchen to Counter — Pt.1,"The midday delivery run, kitchen to store",Trays loaded onto the bike at noon,,fresh twice a day.,15-20s,,delivery,1',
                ].join("\n");
                const url = URL.createObjectURL(new Blob([tpl], { type:"text/csv" }));
                const a = document.createElement("a"); a.href=url; a.download="content_template.csv"; a.click();
                URL.revokeObjectURL(url);
              }} style={{ marginTop:10, padding:"7px 14px", borderRadius:6, border:`1px solid ${FRANZ}`, background:CARD, color:FRANZ, fontSize:11, fontFamily:F_MONO, fontWeight:700, cursor:"pointer" }}>⬇ Download template</button>
            </div>
            <div>
              <div style={{ ...LBL, marginBottom:8 }}>Import as brand (used for rows without a brand column)</div>
              <div style={{ display:"flex", gap:8 }}>
                {["franz","tgc"].map(b=>(
                  <button key={b} onClick={()=>{ setBulkBrand(b); if(bulkFile){ const rd=new FileReader(); rd.onload=(ev)=>setBulkPreview(normalizeBulkRows(ev.target.result,b)); rd.readAsText(bulkFile); } }}
                    style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${bulkBrand===b?bc(b):BORDER}`, background:bulkBrand===b?bc(b):"transparent", color:bulkBrand===b?"#fff":MUTED, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                    {b==="franz"?"Franz":"TGC"}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ border:`2px dashed ${BORDER}`, borderRadius:10, padding:24, textAlign:"center" }}>
              <input type="file" accept=".csv" onChange={handleBulkFile} style={{ display:"none" }} id="csvInput"/>
              <label htmlFor="csvInput" style={{ cursor:"pointer", color:FRANZ, fontFamily:F_MONO, fontSize:13, fontWeight:700 }}>{bulkFile?`✓ ${bulkFile.name}`:"Select CSV file"}</label>
            </div>
            {bulkPreview && (
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:TEXT, marginBottom:8 }}>
                  Found: {bulkPreview.reels.length} reels
                  {bulkPreview.errors.length > 0 && <span style={{ color:RED }}> · {bulkPreview.errors.length} skipped</span>}
                </div>
                {bulkPreview.reels.slice(0,6).map((row,i)=>(
                  <div key={i} style={{ padding:"8px 10px", background:i%2===0?SOFT:CARD, borderRadius:6, fontSize:12, display:"flex", gap:8, alignItems:"center", minWidth:0 }}>
                    <span style={{ color:bc(row.brand||"franz"), fontWeight:700, flexShrink:0 }}>{(row.brand||"franz").toUpperCase()}</span>
                    <span style={{ color:MUTED, flexShrink:0 }}>{row.date}</span>
                    <b style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row.title}</b>
                    {row.assignee && <Chip text={row.assignee} color={aColor(row.assignee)} size={10}/>}
                  </div>
                ))}
                {bulkPreview.reels.length > 6 && <div style={{ fontSize:11.5, color:MUTED, marginTop:6 }}>… and {bulkPreview.reels.length-6} more</div>}
                {bulkPreview.errors.slice(0,4).map((er,i)=>(<div key={i} style={{ fontSize:11.5, color:RED, marginTop:4 }}>{er}</div>))}
              </div>
            )}
          </div>
        </Modal>
      )}

      <Header m={m} saving={saving} todayLong={todayLong}
        who={user ? ((profiles[user.id]?.name) || user.email) : null}
        whoTitle={user?.email} role={role}
        brand={brand} setBrand={setBrand} tab={tab} setTab={setTab} tabs={TABS}
        onPassword={()=>{setShowPwd(true);setPwdMsg(null);}} onSignOut={()=>signOut()}/>

      {/* ── Content ── */}
      <div style={{ padding:m?"14px 10px":"22px 28px", maxWidth:1200, margin:"0 auto" }}>
        {error && <ErrorBanner msg={error} onDismiss={()=>setError(null)}/>}
        {loading ? <Spinner/> : (
          <>
            {/* HEUTE */}
            {tab==="overview" && (
              <Overview reels={brandReels} brand={brand} m={m}
                onOpenReel={(reel,brand)=>setDetailReel({reel,brand})}/>
            )}

            {tab==="analytics" && (
              <AnalyticsPage reels={brandReels} anMap={anMap} loading={anLoading} role={role} m={m}
                onOpenReel={(reel,brand)=>setDetailReel({reel,brand})}/>
            )}

            {/* KALENDER */}
            {tab==="calendar" && (
              <div>
                {/* Stats */}
                <div style={{ display:"grid", gridTemplateColumns:m?"1fr 1fr 1fr":"1fr 1fr 1fr 1fr 1fr", gap:m?8:12, marginBottom:m?14:24 }}>
                  {[
                    { label:"Posted",   val:totalPosted,  color:GREEN },
                    { label:"Filmed",   val:totalFilmed,  color:AMBER },
                    { label:"Planned",   val:totalPlanned, color:MUTED },
                    { label:"Franz ✓",   val:reels.filter(r=>r.brand==="franz"&&r.status==="posted").length, total:reels.filter(r=>r.brand==="franz").length, color:FRANZ },
                    { label:"TGC ✓",     val:reels.filter(r=>r.brand==="tgc"&&r.status==="posted").length,   total:reels.filter(r=>r.brand==="tgc").length,   color:TGC   },
                  ].map((s,i)=>(
                    <div key={i} style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:10, padding:m?10:16, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
                      <div style={{ fontSize:9, color:MUTED, letterSpacing:"1.5px", textTransform:"uppercase", fontFamily:F_MONO, marginBottom:5 }}>{s.label}</div>
                      <div style={{ fontSize:m?22:32, fontWeight:700, color:s.color, lineHeight:1 }}>{s.val}{s.total!==undefined&&<span style={{ fontSize:m?11:14, color:MUTED }}>/{s.total}</span>}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:14, padding:m?12:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)", marginBottom:m?12:16 }}>
                  {!m && <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:8 }}>
                    <button onClick={()=>setShowAddReel(true)} style={{ padding:"8px 16px", borderRadius:8, border:`1px solid ${FRANZ}`, background:`${FRANZ}11`, color:FRANZ, fontSize:11, fontFamily:F_MONO, cursor:"pointer" }}>+ REEL</button>
                  </div>}
                  <CalendarGrid reels={reels} onDayClick={(day,year,month)=>setCalendarDay({day,year,month})}/>
                </div>
              </div>
            )}

            {/* REELS */}
            {tab==="plan" && (
              <ContentPlan reels={reels} brand={brand} m={m}
                onOpenReel={(reel,b)=>setDetailReel({reel,brand:b})}
                onAdd={()=>{ setNewReel(p=>({...p, brand:wBrand})); setShowAddReel(true); }}/>
            )}

            {/* SERIEN */}
            {tab==="series" && (
              <SerienTab series={series} reels={reels}
                onOpenReel={(reel,brand)=>setDetailReel({reel,brand})}
                onToggleStatus={handleToggleStatus}
                role={role} onAdd={handleAddSeries} onDelete={handleDeleteSeries}
                saving={saving}/>
            )}
            {tab==="briefing" && (
              <BriefingTab/>
            )}
          </>
        )}
      </div>

      {/* ── Mobile Bottom Nav ── */}
      {m && (
        <div style={{ position:"fixed", bottom:0, left:0, right:0, background:CARD, borderTop:`1px solid ${BORDER}`, display:"flex", zIndex:60, boxShadow:"0 -4px 20px rgba(0,0,0,0.08)" }}>
          {TABS.map(([id,icon,label,short])=>(
            <button key={id} onClick={()=>setTab(id)} style={{ flex:1, padding:"12px 4px 18px", background:"transparent", border:"none", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
              <span style={{ fontSize:22 }}>{icon}</span>
              <span style={{ fontSize:10, fontFamily:F_BODY, color:tab===id?TEXT:MUTED, fontWeight:tab===id?700:500 }}>{short || label}</span>
              {tab===id && <div style={{ width:24, height:2, background:TEXT, borderRadius:1, marginTop:2 }}/>}
            </button>
          ))}
        </div>
      )}

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
    </div>
  );
}
// ══════════════════════════════════════════════════════════════
// AppGate — top-level: requires login, then renders the Dashboard.
// ══════════════════════════════════════════════════════════════
export default function AppGate() {
  const [session, setSession]   = useState(undefined); // undefined = still checking
  const [profile, setProfile]   = useState(null);
  const [profiles, setProfiles] = useState({});

  useEffect(() => {
    getSession().then(setSession).catch(() => setSession(null));
    const unsub = onAuthChange((s) => setSession(s));
    return unsub;
  }, []);

  useEffect(() => {
    if (!session) { setProfile(null); return; }
    getMyProfile().then(setProfile).catch(() => {});
    fetchProfiles().then(setProfiles).catch(() => {});
  }, [session]);

  if (session === undefined) {
    return <div style={{ minHeight:"100vh", background:BG, display:"flex", alignItems:"center", justifyContent:"center", color:MUTED, fontFamily:F_MONO }}>Loading…</div>;
  }
  if (!session) return <Auth />;

  return <Dashboard user={session.user} role={profile?.role || "creator"} profiles={profiles} />;
}
